import { useState, useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { InputNumber, Select, AutoComplete, Input, Button, Checkbox, message, Modal } from 'antd';
import { DeleteOutlined, CaretDownOutlined, CaretRightOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getFabCount, setFabCount,
  buildFabricationColumns, selectGridFabrications,
  buildProformaColumns, computeColumnLayout,
  displayDimensionInUnit, normalizeDimensionByInputUnit, UNIT_PRECISION,
  parseThicknessMm,
  decimalToFraction16, parseFractionInput, isValidInchInput, filterInchChar,
  createUuid, blankLine, rebuildTree, descEnd, insertRelative, groupSheetChildren, computeTreeRows,
  isPieceType, resolveItemRateForDoc, syncRatePair,
  chargeStampForActual, resolveSheetCostMode, matchJumboCondition
} from './proformaHelpers';
import { proformaApi } from './proformaApi';
import { LINE_TYPE, isLamiOrDgu, nestRowTint } from './proformaNestingMasters';
import { tabSelectActiveOption } from '../../../../utils/typeaheadTab';
import LamiConfigModal from './LamiConfigModal';
import DguConfigModal from './DguConfigModal';
import SmartItemDescCell, { hasRftProcess } from './SmartItemDescField';
import GlassConfigModal from './GlassConfigModal';
import PriceRationaleModal from './PriceRationaleModal';
import NarrationModal from './NarrationModal';
import EdgePickerModal from './EdgePickerModal';

// ════════════════════════════════════════════════════════════════════
// Stage 6Q.2 — GlassMaster-style nesting grid (custom, no AntD Table).
//
// The line list is a PRE-ORDER tree: each row carries refUuid / parentRefUuid
// / level / type. Right-click drives nesting: Set as Sheet / LAMI / DGU /
// LAMI+DGU (LAMI/DGU open config popups that GENERATE child rows), Add Child
// Row, Add/Edit Narration, Glass Config, Delete. SHEET masters carry cut-piece
// children + a scrap/utilization "NESTING" bar. Pricing is child-priced: a
// parent row = Σ its children (computed in proformaHelpers.computeTreeRows).
// ════════════════════════════════════════════════════════════════════

const ROW_H = 28;
const GROUP_H = 26;
// Shared frozen fallback for a missing computed view. The priced `views` can lag
// the live tree by one render (see the deferred preview in ProformaEditorPage);
// reusing ONE object for the gap avoids allocating a fresh `{}` per render, which
// would bust the GridRow memo for the transiently-unpriced row.
const EMPTY_VIEW = Object.freeze({});
const LEAF_H = 26;
const HEADER_H = GROUP_H + LEAF_H;
const MAX_LINES = 1500;
const VROW_BUFFER = 10;
const NAV_KINDS = ['text', 'num', 'int', 'fab', 'dim', 'glasstype', 'item'];
// '*' is a sentinel meaning "copy the WHOLE row" (every value field except the
// tree-structure / identity fields below). No column uses it by default now —
// kept for the LAMI/DGU assembly-copy path and as an available option.
const FILL_WHOLE_ROW = '*';
// ITEM-DESC drag-copy carries the DESCRIPTION plus the value-add it ENCODES. The
// #process@rate tags in a description parse into `processPricing` (the same field a
// normal edit derives via SmartItemDescField), which drives each line's Value-Added
// Services. Copying the text WITHOUT processPricing left the target's value-add stale
// (0) — the reported "value added services not triggered on drag/apply" bug. So the
// fill copies the composed + raw text AND the derived processPricing, exactly as
// typing that description would (a description with no tags clears the target's stale
// processPricing → value-add 0). Every OTHER column — dimensions, qty, rates,
// fabrication, manual charge sizes, discounts, GST — has its OWN cell/drag handle and
// stays exactly as each target row already has it.
const ITEM_DESC_FILL_FIELDS = ['itemDesc', 'rawItemDesc', 'processPricing'];
const FILL_FIELDS = {
  type: ['type'], location: ['location'], itemDesc: ITEM_DESC_FILL_FIELDS,
  // PRICE columns — drag the handle to copy the rate down (value only, not the
  // whole row). alternateRate only exists on area-priced (SQM/SQFT) documents.
  salesRate: ['salesRate'], alternateRate: ['alternateRate']
};
// Identity + tree-structure fields that must NEVER be copied by a whole-row fill
// (each row keeps its own position/identity; only the data is duplicated).
const ROW_STRUCTURE_FIELDS = new Set([
  'refUuid', 'parentRefUuid', 'level', 'lineNo', 'rowNo', 'hasChildren', 'isExpanded'
]);
// Computed OUTPUT fields that must be RESET (not carried) onto a copied row.
// computeLeafRow TRUSTS a line's stored `otherChargeAmount` — it is produced by the
// async Other-Charges /preview, not recomputed in the pure engine — so a clone that
// keeps the source's value bakes a stale (and for a document-level charge like
// Handling/Documentation, DUPLICATED) Other-Charge into the clone's taxable + GST,
// inflating the subtotal, GST and grand total. Reset them so the OC engine
// recomputes each copy's real charge and the summary reconciles correctly. Every
// OTHER money field (basic / fabrication / value-add / taxable / GST / scrap /
// parent roll-ups) IS recomputed downstream, so it self-corrects and needs no reset.
const CLONE_RESET_FIELDS = new Set(['otherChargeAmount', 'otherChargeDetails']);
function resetClonedComputed(row) {
  row.otherChargeAmount = 0;
  row.otherChargeDetails = [];
  return row;
}

function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }
// Deep-clone a value so copied object fields (fabricationDetails / processPricing /
// nestingConfig / itemSnapshot) are independent — editing one target row must not
// mutate the source or its siblings.
function cloneVal(v) {
  if (v == null || typeof v !== 'object') return v;
  try {
    if (typeof structuredClone === 'function') return structuredClone(v);
  } catch (_e) { /* fall through */ }
  return JSON.parse(JSON.stringify(v));
}
// Build the copy patch from a source row for the given fields. The '*' sentinel
// copies every field except identity/structure; otherwise only the named fields.
// Object values are deep-cloned so target rows don't share references.
function buildFillPatch(src, fields) {
  const patch = {};
  if (Array.isArray(fields) && fields.includes(FILL_WHOLE_ROW)) {
    for (const k of Object.keys(src)) {
      if (ROW_STRUCTURE_FIELDS.has(k)) continue;
      if (CLONE_RESET_FIELDS.has(k)) continue;    // don't carry stale/duplicated OC
      patch[k] = cloneVal(src[k]);
    }
    // Reset the OC so the fill target's charge is recomputed live, not inherited.
    return resetClonedComputed(patch);
  }
  (fields || []).forEach((f) => { patch[f] = cloneVal(src[f]); });
  return patch;
}

// ── LAMI/DGU "Copy unit down to…" (right-click) ─────────────────────────────
// A LAMI / DGU / LAMI+DGU cut-piece that sits DIRECTLY under a SHEET master can
// be replicated onto the cut-piece rows below it within the SAME sheet group.
// planUnitCopy returns null unless the source qualifies; `targets` are the
// direct, NON-SHEET (cut-piece) children of that master following the source's
// own subtree — the only slots eligible to be replaced. Stock sheet-size rows
// (type SHEET) and anything outside the group are never touched.
function planUnitCopy(rows, sourceRefUuid) {
  const list = Array.isArray(rows) ? rows : [];
  const sIdx = list.findIndex((r) => r && r.refUuid === sourceRefUuid);
  if (sIdx < 0) return null;
  const src = list[sIdx];
  if (!isLamiOrDgu(src.type)) return null;
  const master = list.find((r) => r && r.refUuid === src.parentRefUuid);
  if (!master || typeUp(master.type) !== 'SHEET') return null;
  const masterIdx = list.findIndex((r) => r && r.refUuid === master.refUuid);
  if (masterIdx < 0) return null;
  const groupEnd = descEnd(list, masterIdx);
  const sourceEnd = descEnd(list, sIdx);
  const targets = [];
  let i = sourceEnd;
  while (i < groupEnd) {
    const r = list[i];
    const end = descEnd(list, i);
    if (r && r.parentRefUuid === master.refUuid && typeUp(r.type) !== 'SHEET') {
      targets.push({ idx: i, end });
    }
    i = Math.max(end, i + 1);
  }
  return { masterRef: master.refUuid, sIdx, sourceEnd, targets };
}

// Deep-clone a unit block ([root, …descendants] in pre-order) with fresh
// refUuids, re-parenting the root to `newParentRef` and preserving each row's
// `level` (source & target sit at the same depth). Object fields are deep-cloned
// so the copy shares no references; rebuildTree recomputes rowNo/hasChildren.
function cloneUnitBlock(block, newParentRef) {
  const idMap = new Map();
  block.forEach((r) => idMap.set(r.refUuid, createUuid()));
  return block.map((r, i) => {
    const c = {};
    for (const k of Object.keys(r)) c[k] = cloneVal(r[k]);
    c.refUuid = idMap.get(r.refUuid);
    c.parentRefUuid = i === 0 ? newParentRef : (idMap.get(r.parentRefUuid) || newParentRef);
    // A clone must NOT inherit the source's stored Other-Charge — computeLeafRow
    // trusts it (it comes from the async OC /preview), so carrying it double-counts
    // document-level charges and inflates the totals. Reset so it recomputes fresh.
    return resetClonedComputed(c);
  });
}

// ── Drag-copy a LAMI/DGU/LAMI+DGU assembly PARENT onto the rows below ────────
// The whole assembly block (parent + its layer/pane children) is cloned onto the
// sibling "unit" rows in the dragged range — each target is REPLACED by an exact
// copy (same replace-semantics as the right-click "Copy unit down to…", now on
// the drag handle). Generalises planUnitCopy to two contexts:
//   • top-level assembly  → siblings = other TOP-LEVEL rows (a plain glass line
//     or another assembly); each is replaced with a clone of the whole block.
//   • sheet-nested assembly → siblings = the SHEET master's non-SHEET cut-piece
//     children (the exact slots the right-click copy already targets).
// Stock SHEET rows / SHEET masters are never replaced, and a top-level SHEET
// nesting group in the range is skipped whole. `dropRefUuid` bounds the range: a
// sibling is a target when its root sits at or above the drop row. Returns null
// (a no-op) unless the source is an assembly and ≥1 target lies in range.
function planAssemblyDragCopy(rows, sourceRefUuid, dropRefUuid) {
  const list = Array.isArray(rows) ? rows : [];
  const sIdx = list.findIndex((r) => r && r.refUuid === sourceRefUuid);
  if (sIdx < 0) return null;
  const src = list[sIdx];
  if (!isLamiOrDgu(src.type)) return null;
  const dropIdx = list.findIndex((r) => r && r.refUuid === dropRefUuid);
  if (dropIdx <= sIdx) return null;
  const sourceEnd = descEnd(list, sIdx);
  const parentRef = src.parentRefUuid || null;
  // Scope siblings to the source's group: the SHEET-master subtree when nested,
  // else the whole list (top-level).
  let groupEnd = list.length;
  if (parentRef) {
    const mIdx = list.findIndex((r) => r && r.refUuid === parentRef);
    if (mIdx < 0) return null;
    groupEnd = descEnd(list, mIdx);
  }
  const targets = [];
  let i = sourceEnd;
  while (i < groupEnd) {
    const r = list[i];
    const end = descEnd(list, i);
    const sameGroup = (r.parentRefUuid || null) === parentRef;
    const isSheetRow = typeUp(r.type) === 'SHEET';
    if (sameGroup && !isSheetRow && i <= dropIdx) targets.push({ idx: i, end });
    i = Math.max(end, i + 1);
  }
  if (targets.length === 0) return null;
  return { sIdx, sourceEnd, parentRef, targets };
}

function gridMoney(n) { return num(n).toFixed(2); }
// Dimension display (MM/CM/FT/IN): format to the unit's precision but DROP
// trailing zeros, so a whole number (and .0 / .00) shows with no decimal point.
function fmtDim(n, unit) {
  const s = num(n).toFixed(UNIT_PRECISION[unit] || 2);
  return s.indexOf('.') === -1 ? s : s.replace(/0+$/, '').replace(/\.$/, '');
}
function typeUp(t) { return String(t || '').toUpperCase(); }

// Cell validation — block non-numeric keystrokes in numeric cells so letters /
// symbols (e.g. "ffff") can never be typed or pasted into a dimension, qty,
// rate or fabrication-count cell. Digits always pass; a single dot passes when
// decimals are allowed; control / navigation keys (length > 1) pass through to
// the grid's own keyboard handler.
function makeNumericKeyGuard(allowDecimal) {
  return (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const k = e.key;
    if (k == null || k.length !== 1) return;               // Backspace, Tab, Arrow…
    if (k >= '0' && k <= '9') return;
    if (allowDecimal && k === '.' && !String(e.currentTarget.value || '').includes('.')) return;
    e.preventDefault();
  };
}
const guardDecimal = makeNumericKeyGuard(true);    // dimensions, rates, %
const guardInteger = makeNumericKeyGuard(false);   // qty, fabrication counts
// Reject a pasted value that isn't a clean number (keeps the prior value).
function blockNonNumericPaste(allowDecimal) {
  const re = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
  return (e) => {
    const txt = (e.clipboardData || window.clipboardData).getData('text');
    if (!re.test(String(txt).trim())) e.preventDefault();
  };
}
const pasteDecimal = blockNonNumericPaste(true);
const pasteInteger = blockNonNumericPaste(false);

const GRID_FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif";
const GRID_CSS = `
.pfg-wrap{font-family:${GRID_FONT};color:#0f172a;}
.pfg-toolbar{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;}
.pfg-title{font-size:13px;font-weight:650;color:#0f172a;letter-spacing:-.01em;}
/* Height fits at least 20 rows: header (52px) + 20 × ROW_H (28px) = 612px → 620px
   floor; taller screens grow via the calc so more rows show. */
.pfg-scroll{position:relative;overflow:auto;max-height:max(620px, calc(100vh - 300px));border:1px solid #cbd5e1;background:#fff;border-radius:6px;box-shadow:0 1px 2px rgba(15,23,42,.04);}
.pfg-scroll::-webkit-scrollbar{width:11px;height:11px;}
.pfg-scroll::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:6px;border:3px solid #fff;}
.pfg-scroll::-webkit-scrollbar-thumb:hover{background:#94a3b8;}
.pfg-grid{position:relative;}
.pfg-header{position:sticky;top:0;z-index:20;}
.pfg-hgroups,.pfg-hleaves{display:flex;}
.pfg-ghdr,.pfg-lhdr{display:flex;flex:0 0 auto;align-items:center;justify-content:center;box-sizing:border-box;white-space:nowrap;overflow:hidden;text-transform:uppercase;}
.pfg-ghdr{background:#eef2f7;color:#475569;font-size:10px;font-weight:700;letter-spacing:.05em;border-right:1px solid #dbe2ea;border-bottom:1px solid #cbd5e1;}
.pfg-lhdr{background:#f6f8fb;color:#475569;font-size:10px;font-weight:600;letter-spacing:.03em;border-right:1px solid #e6ebf1;border-bottom:1px solid #cbd5e1;}
.pfg-lhdr.ch,.pfg-ghdr.ch{background:#e9f1ff;}
.pfg-lhdr.fab,.pfg-ghdr.fab{background:#fff5e9;}
.pfg-lhdr.pr,.pfg-ghdr.pr{background:#ecfdf3;}
.pfg-lhdr.cu,.pfg-ghdr.cu{background:#fff5e9;}
.pfg-hfix{z-index:5;}
.pfg-hfix-last{border-right:1px solid #e6ebf1;}
.pfg-row{display:flex;position:relative;box-sizing:border-box;background:#fff;}
.pfg-row:hover{background:#f5f8fc;}
/* Serial column follows the active row — soft blue fill + bold number on
   cursor-hover or keyboard/edit focus. Pure CSS (:hover / :focus-within): no
   React state and no re-render, so it costs the virtualized grid nothing. */
.pfg-row:hover .pfg-serial,
.pfg-row:focus-within .pfg-serial{background:#dbeafe;}
.pfg-row:hover .pfg-serial-no,
.pfg-row:focus-within .pfg-serial-no{color:#1d4ed8;}
.pfg-row.pfg-parent{font-weight:600;}
/* WO-3.3 — Change-Order fate rows: struck original (line-through, muted) + a
   cancelled cut-piece row (soft red wash). Both are locked (read-only cells). */
.pfg-row.pfg-struck{background:#eceef1;color:#64748b;}
.pfg-row.pfg-struck:hover{background:#e6e9ee;}
.pfg-row.pfg-struck .pfg-cell{text-decoration:line-through;text-decoration-color:#94a3b8;}
.pfg-row.pfg-struck .pfg-col-item{text-decoration:none;}
.pfg-row.pfg-cancelled{background:#fdeaea;color:#334155;}
.pfg-row.pfg-cancelled:hover{background:#fbdede;}
/* WO-3.3 — locked fate cells render as AntD *disabled* inputs, whose default text
   is a washed-out rgba(0,0,0,.25). Force a readable ink (incl. -webkit-text-fill-
   color, which AntD sets on disabled fields) so the struck original + cancelled
   cut-piece lines stay legible. Editable BILL cells keep their own darker ink. */
.pfg-row.pfg-struck :is(.ant-input-number-input,.ant-input,.ant-select-selection-item,.pfg-serial,.pfg-serial-no,.pfg-fab-mirror),
.pfg-row.pfg-struck .pfg-col-item{color:#64748b!important;-webkit-text-fill-color:#64748b!important;}
.pfg-row.pfg-cancelled :is(.ant-input-number-input,.ant-input,.ant-select-selection-item,.pfg-serial,.pfg-serial-no,.pfg-fab-mirror),
.pfg-row.pfg-cancelled .pfg-col-item{color:#334155!important;-webkit-text-fill-color:#334155!important;}
.pfg-tw{width:100%;height:100%;border:none;background:transparent;cursor:pointer;color:#2563eb;font-size:11px;line-height:1;display:flex;align-items:center;justify-content:center;padding:0;}
.pfg-tw:hover{color:#1d4ed8;}
.pfg-pmark{font-size:9px;color:#2563eb;font-weight:700;margin-right:2px;}
.pfg-cell{position:relative;flex:0 0 auto;box-sizing:border-box;height:100%;border-right:1px solid #eef1f5;border-bottom:1px solid #eef1f5;overflow:hidden;display:flex;align-items:center;background:inherit;}
.pfg-cell.ch{background:#f6faff;}.pfg-cell.fab{background:#fffbf4;}.pfg-cell.pr{background:#f5fdf8;}.pfg-cell.cu{background:#fffcf6;}
/* TYPE + ITEM DESC columns — defined left+right border so the free-text columns read as framed. */
.pfg-cell.pfg-col-item{border-left:1px solid #cbd5e1;border-right:1px solid #cbd5e1;}
/* Same blue focus border as the numeric cells (the pfg-ac selector suppresses its
   own focus ring, so draw it on the cell wrapper instead). */
.pfg-cell.pfg-col-item:focus-within{box-shadow:inset 0 0 0 2px #2563eb;border-radius:2px;background:#fff;z-index:4;}
.pfg-fixed{position:sticky;z-index:6;background:inherit;}
.pfg-fixed-last{border-right:1px solid #eef1f5;}
.pfg-fixed-r{position:sticky;right:0;z-index:6;background:inherit;border-left:1px solid #cbd5e1;justify-content:center;}
.pfg-serial{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:11px;font-weight:500;font-variant-numeric:tabular-nums;}
.pfg-serial--parent{justify-content:flex-start;gap:2px;padding-right:2px;}
.pfg-serial-no{color:#64748b;font-size:11px;font-weight:600;font-variant-numeric:tabular-nums;}
.pfg-tree-toggle{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;padding:0;border:none;background:transparent;cursor:pointer;color:#64748b;border-radius:3px;font-size:11px;line-height:1;flex:0 0 auto;}
.pfg-tree-toggle:hover{background:#e2e8f0;color:#1e293b;}
.pfg-fab-mirror{display:flex;align-items:center;justify-content:center;width:100%;height:100%;font-size:11px;font-variant-numeric:tabular-nums;color:#94a3b8;}
.pfg-fab-mirror.has{color:#9333ea;background:#faf5ff;font-weight:600;}
.pfg-ro{width:100%;padding:0 8px;text-align:right;font-size:12px;color:#1e293b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-variant-numeric:tabular-nums;}
.pfg-ro.basic{font-weight:600;color:#1d4ed8;}
.pfg-ro.tot{font-weight:700;color:#15803d;}
.pfg-ro.oc{color:#b45309;}
.pfg-nestbar{position:relative;height:28px;box-sizing:border-box;background:linear-gradient(90deg,#fef9c3 0%,#fefce8 100%);border-bottom:2px solid #d97706;}
.pfg-nestinner{position:sticky;left:0;display:inline-flex;align-items:center;gap:10px;height:100%;padding:0 12px;font-size:10px;color:#78350f;}
.pfg-scm{display:inline-flex;align-items:center;border:1px solid #cbd5e1;border-radius:5px;overflow:hidden;background:#fff;}
.pfg-scm__opt{appearance:none;border:none;background:transparent;cursor:pointer;font:inherit;font-size:10px;font-weight:700;letter-spacing:.02em;padding:2px 9px;color:#64748b;line-height:1.4;}
.pfg-scm__opt+.pfg-scm__opt{border-left:1px solid #e2e8f0;}
.pfg-scm__opt.is-on{background:#1d4ed8;color:#fff;}
.pfg-scm__opt:disabled{cursor:not-allowed;opacity:.6;}
.pfg-scm__opt:not(.is-on):not(:disabled):hover{background:#eff6ff;color:#1d4ed8;}
.pfg-scraprate.ant-input-number{height:20px;background:#fffbeb;border-color:#d97706;border-radius:3px;}
.pfg-scraprate .ant-input-number-input-wrap,.pfg-scraprate .ant-input-number-input{height:18px;line-height:18px;}
.pfg-scraprate .ant-input-number-input{font-size:11px;text-align:right;color:#92400e;font-weight:600;padding:0 4px;}
.pfg-secbar{position:relative;height:28px;box-sizing:border-box;background:linear-gradient(90deg,#dbeafe 0%,#eff6ff 100%);border-bottom:2px solid #3b82f6;}
.pfg-secinner{position:sticky;left:0;display:inline-flex;align-items:center;gap:10px;height:100%;padding:0 12px;}
.pfg-cell .ant-input,.pfg-cell .ant-input-number,.pfg-cell .ant-select-selector{height:26px!important;border:none!important;border-radius:0!important;box-shadow:none!important;background:transparent!important;}
.pfg-cell .ant-input{padding:0 8px!important;font-size:12px;line-height:26px!important;font-family:inherit;}
.pfg-cell .ant-input-number{width:100%!important;}
.pfg-cell .ant-input-number-input{height:26px!important;padding:0 8px!important;text-align:right;font-size:12px;font-variant-numeric:tabular-nums;font-family:inherit;}
.pfg-cell .ant-input-number-handler-wrap{display:none!important;}
.pfg-cell .ant-select{width:100%;font-size:12px!important;}
.pfg-cell .ant-select-selector{padding:0 8px!important;font-size:12px;font-family:inherit;display:flex;align-items:center;}
.pfg-cell .ant-select-selection-search{inset-inline-start:8px!important;inset-inline-end:8px!important;display:flex;align-items:center;}
.pfg-cell .ant-select-selection-search-input{height:26px!important;}
.pfg-cell .ant-select-selection-search-input,.pfg-cell .ant-select-selection-item,.pfg-cell .ant-select-selection-placeholder{font-size:12px!important;font-family:inherit!important;line-height:26px!important;}
.pfg-cell .ant-input:focus,.pfg-cell .ant-input-number-focused,.pfg-cell .ant-input-number:focus-within,.pfg-cell .ant-select-focused .ant-select-selector{background:#fff!important;box-shadow:inset 0 0 0 2px #2563eb!important;border-radius:2px!important;z-index:4;}
.pfg-cell .pfg-ac.ant-select-focused .ant-select-selector{box-shadow:none!important;background:transparent!important;}
.pfg-cell .pfg-ac .ant-select-selector{padding-inline-start:4px!important;}
.pfg-cell .pfg-ac .ant-select-selection-search{inset-inline-start:4px!important;}
/* The ITEM DESC suggestion popup sizes to its content (popupMatchSelectWidth=
   {false}) — give it a comfortable width band so long item descriptions are
   readable while selecting, independent of the narrow ITEM DESC cell. Scoped to
   the -wide variant so the compact TYPE picker keeps matching its own cell. */
.pfg-ac-pop-wide{min-width:440px!important;max-width:min(760px,90vw)!important;}
.pfg-ac-pop .ant-select-item{min-height:24px!important;padding:3px 10px!important;}
.pfg-ac-pop .ant-select-item,.pfg-ac-pop .ant-select-item-option-content{font-size:11px!important;line-height:1.5!important;}
/* Highlighted (hover / keyboard-active) + selected option use the SHEET master
   row tint (#fde68a) so the dropdown highlight matches the grid row colour. */
.pfg-ac-pop .ant-select-item-option-active:not(.ant-select-item-option-disabled),
.pfg-ac-pop .ant-select-item-option-selected:not(.ant-select-item-option-disabled){background-color:#fde68a!important;}
/* Richer "#" process picker row: name · rate · colour-coded UOM badge. Display
   only — the option value (insert/filter text) is unchanged. */
.pfg-ac-pop .ant-select-item-option-content{white-space:normal!important;overflow:visible!important;text-overflow:clip!important;word-break:break-word!important;}
.pfg-proc-opt{display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:10px;width:100%;}
.pfg-proc-name{font-size:12px;font-weight:600;color:#1f2733;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.pfg-proc-rate{font-family:${GRID_FONT};font-size:11.5px;font-weight:600;color:#5b6472;font-variant-numeric:tabular-nums;}
.pfg-proc-norate{color:#9aa3af;font-weight:500;}
.pfg-proc-uom{font-size:9px;font-weight:800;letter-spacing:.03em;padding:1px 6px;border-radius:5px;line-height:14px;}
.pfg-uom-rft{color:#0e7490;background:#e0f5f8;}
.pfg-uom-sqf{color:#7c3aed;background:#f0e9ff;}
.pfg-uom-pcs{color:#b45309;background:#fdf1de;}
.pfg-act{display:flex;align-items:center;justify-content:center;width:100%;height:100%;}
/* Fill handle — bigger, easy-to-grab target with a clear visible grip. The
   whole 16px corner is clickable; the dot is the affordance. Drag to copy down. */
.pfg-fill{position:absolute;right:0;bottom:0;width:16px;height:16px;display:flex;align-items:flex-end;justify-content:flex-end;padding:2px;cursor:ns-resize;opacity:0;z-index:6;transition:opacity .1s;}
.pfg-fill::after{content:"";width:9px;height:9px;background:#2563eb;border:1.5px solid #fff;border-radius:3px;box-shadow:0 1px 3px rgba(15,30,60,.35);}
.pfg-cell:hover .pfg-fill,.pfg-cell:focus-within .pfg-fill{opacity:1;}
.pfg-fill:hover::after{background:#1d4ed8;width:11px;height:11px;}
.pfg-cell.pfg-fillhi{box-shadow:inset 0 0 0 1px #2563eb;background:#eef5ff!important;}
/* ITEM DESC header resize grip — sits on the column's right edge. */
.pfg-colresize{position:absolute;top:0;right:0;width:8px;height:100%;cursor:col-resize;z-index:8;touch-action:none;}
.pfg-colresize::after{content:"";position:absolute;top:2px;bottom:2px;right:0;width:2px;border-radius:2px;background:transparent;transition:background .1s;}
.pfg-colresize:hover::after,.pfg-colresize:active::after{background:#2563eb;}
.pfg-ctx{position:fixed;z-index:200;background:#fff;border:1px solid #e2e8f0;border-radius:9px;box-shadow:0 12px 34px rgba(15,23,42,.16),0 2px 8px rgba(15,23,42,.06);min-width:250px;overflow:hidden;padding:3px 4px;font-family:${GRID_FONT};}
/* Section header — small uppercase label that groups the actions below it. */
.pfg-ctx .grp{display:block;padding:5px 10px 1px;font-size:9px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:#94a3b8;pointer-events:none;}
.pfg-ctx .grp:first-child{padding-top:1px;}
.pfg-ctx button{display:flex;align-items:center;justify-content:space-between;gap:16px;width:100%;border:none;background:transparent;padding:4px 10px;font-size:12px;line-height:1.2;cursor:pointer;color:#1e293b;border-radius:5px;transition:background .08s;}
/* Leading glyph (emoji / symbol) gets a fixed gutter so every label starts on the
   same vertical line whether or not the item has an icon. */
.pfg-ctx button .ico{flex:0 0 18px;text-align:center;font-size:12px;color:#7c3aed;margin-right:-4px;}
.pfg-ctx button .lbl{flex:1 1 auto;text-align:left;white-space:nowrap;}
.pfg-ctx button:hover{background:#f1f5f9;}
.pfg-ctx button.del{color:#dc2626;}.pfg-ctx button.del .ico{color:#dc2626;}.pfg-ctx button.del:hover{background:#fef2f2;}
.pfg-ctx button.set{color:#6d28d9;}.pfg-ctx button.set:hover{background:#f5f3ff;}
.pfg-ctx button:disabled{opacity:.4;cursor:default;}
.pfg-ctx button:disabled:hover{background:transparent;}
.pfg-ctx .sep{height:1px;background:#eef2f7;margin:3px 6px;}
.pfg-ctx kbd{flex:0 0 auto;font-size:9px;color:#94a3b8;font-family:${GRID_FONT};background:#f8fafc;border:1px solid #e6ebf1;border-radius:4px;padding:1px 6px;letter-spacing:.02em;}
`;

function parseCellId(id) {
  const m = /^pfc-(\d+)-(\d+)$/.exec(id || '');
  return m ? [parseInt(m[1], 10), parseInt(m[2], 10)] : null;
}

// PERF — referential-identity guard for computed row `view`s. computeTreeRows
// allocates a FRESH view object for every row on each recompute; handed straight
// to the memoized GridRow, that changes the `view` prop identity on ALL rows, so
// the whole (AntD-heavy) grid re-renders on every keystroke even though only one
// row's numbers moved. Reusing the previous view object when every own field is
// strictly === lets the GridRow memo hold for untouched rows. Values are
// byte-identical — this changes render scheduling only, never a computed number.
// A view carrying a nested object/array (LAMI mirrored-fab entries, SHEET material
// breakdown) always gets a fresh nested ref from computeTreeRows, so it compares
// unequal and safely re-renders — a preserved identity can never show stale data.
function shallowEqualView(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) { if (a[k] !== b[k]) return false; }
  return true;
}

function glassTypeOptions(glassTypes) {
  return (Array.isArray(glassTypes) ? glassTypes : [])
    .filter((g) => g && g.id != null && (g.label || g.code) && (g.label || g.code) !== 'All')
    .map((g) => ({ value: g.label || g.code, label: g.label || g.code }));
}

// The TYPE column shows the glass type by LABEL, but the Other-Charges rule
// engine filters on the glass type CODE (rule.glassTypeCodes holds codes). So
// when a glass type is committed we resolve the chosen label/code back to the
// master CODE and store it in `glassTypeCode` — otherwise glass-type rules
// (Template / Shape charge, etc.) never match and show "· n/a".
function resolveGlassTypeCode(value, glassTypes) {
  const v = value == null ? '' : String(value).trim();
  if (!v) return null;
  const list = Array.isArray(glassTypes) ? glassTypes : [];
  const m = list.find((g) => g && (
    String(g.code || '').toUpperCase() === v.toUpperCase()
    || String(g.label || '').toUpperCase() === v.toUpperCase()
  ));
  return m ? (m.code || m.label || v) : v;
}

// Non-glass line types selectable in the TYPE column. HARDWARE / SERVICES turn a
// row into a per-piece line (rate × qty from the Item Master, no dimensions).
const PIECE_TYPE_OPTIONS = [
  { value: 'HARDWARE', label: 'HARDWARE' },
  { value: 'SERVICES', label: 'SERVICES' }
];

function TypeCell({ cellId, value, glassTypes, readOnly, onCommit }) {
  const [opts, setOpts] = useState([]);
  const all = useMemo(() => [...PIECE_TYPE_OPTIONS, ...glassTypeOptions(glassTypes)], [glassTypes]);
  const search = useCallback((text) => {
    const q = (text == null ? '' : String(text)).toLowerCase();
    setOpts(all.filter((o) => !q || o.value.toLowerCase().includes(q)).slice(0, 20));
  }, [all]);
  return (
    <AutoComplete id={cellId} className="w-full pfg-ac" popupClassName="pfg-ac-pop" value={value || ''} disabled={readOnly}
      options={opts} filterOption={false} onSearch={search}
      onFocus={() => setOpts([])} onBlur={() => setOpts([])}
      onChange={(v) => onCommit(v)} placeholder="Glass type" />
  );
}

function DimCell({ cellId, mm, unit, readOnly, onCommit, placeholderMm }) {
  // Show the manual override if typed; otherwise show the auto (regular-workflow)
  // charge size as a NORMAL value (not dimmed). The auto value is NOT committed —
  // the underlying data stays blank until the user actually types — so an
  // un-edited cell keeps following the wastage workflow and reacts to actual-size
  // changes. Clearing the cell snaps it back to the auto value.
  const effMm = mm > 0 ? mm : num(placeholderMm);
  const display = effMm > 0 ? displayDimensionInUnit(effMm, unit) : null;
  const [local, setLocal] = useState('');
  // While the inch cell is focused, `local` is the single source of truth — an
  // EMPTY `local` means the user deliberately cleared it and must stay empty.
  // (Without this flag the value fell back to `shown` the instant local === '',
  // so clearing snapped the old fraction straight back and the cell wouldn't
  // empty.) When not editing, show the computed fraction.
  const [editing, setEditing] = useState(false);
  if (unit !== 'IN') {
    return (
      <InputNumber id={cellId} className="w-full" min={0} controls={false}
        value={display != null ? display : undefined} disabled={readOnly}
        onKeyDown={guardDecimal} onPaste={pasteDecimal}
        onChange={(val) => onCommit(normalizeDimensionByInputUnit(val || 0, unit))} />
    );
  }
  const shown = display != null ? decimalToFraction16(display) : '';
  return (
    <Input id={cellId} value={editing ? local : shown} disabled={readOnly}
      autoComplete="off" placeholder="0"
      onFocus={() => { setEditing(true); setLocal(shown); }}
      onChange={(e) => setLocal(filterInchChar(e.target.value))}
      onBlur={() => {
        const t = local.trim();
        setEditing(false);
        if (t === '') { onCommit(0); setLocal(''); return; }
        if (!isValidInchInput(t)) { setLocal(''); return; }
        const parsed = parseFractionInput(t);
        onCommit(parsed != null ? normalizeDimensionByInputUnit(parsed, 'IN') : 0);
        setLocal('');
      }} />
  );
}

// One tree row. `row` already carries rowNo / hasChildren / level / type (from
// rebuildTree) and `view` is its computed (possibly rolled-up) view.
const GridRow = memo(function GridRow({
  row, view, vi, columns, colLeft, colRight, lastFixedLeftKey, totalWidth, ro: roIn, linesLen, priceUomCode,
  dropdowns, gstOptions, navKeys, updateRow, removeRow, pickItem, pickCombo, startFillDrag,
  fillHi, fillHiCol, onContextMenu, onToggle, selected, onToggleSelect, onRftAdded, jumboCondition
}) {
  const tU = typeUp(row.type);
  const isParent = row.hasChildren === true;
  // WO-3.3 — Change-Order fate rows are locked in the grid: a STRUCK original and a
  // CANCELLED cut-piece line are frozen records; a committed released new-size line
  // is in production. They render read-only (with a line-through / tag below) so
  // they cannot be silently edited. Ordinary LIVE lines are unaffected.
  const coState = String(row.lineState || '').toUpperCase();
  const struckCO = coState === 'STRUCK';
  const cancelledCO = coState === 'CANCELLED';
  const committedNew = row.isCommitted === true && !struckCO && !cancelledCO;
  const ro = roIn || struckCO || cancelledCO || committedNew;
  // WO-3.3 — a CANCELLED cut-piece line BILLED to the customer stays frozen EXCEPT
  // its commercial cells: glass type, item description, price and fabrication. Those
  // are tuned in the revision after the Change Order (the piece is delivered as-is at
  // a value the operator sets), so they use the grid-level lock (`roIn`, normally
  // false) instead of the row lock. Struck / scrap / off-cut / committed lines are
  // never unlocked. `roType`/`roItem`/`roFab` gate the type + item + fabrication
  // cells; the price cell gates inline (price fields only). The backend mirrors this
  // exactly (enforcePersistedFate) and rejects a structural type on a bill line.
  const billLine = cancelledCO && String(row.cancelDisp || '').toLowerCase() === 'bill';
  const roType = billLine ? roIn : ro;
  const roItem = billLine ? roIn : ro;
  const roFab = billLine ? roIn : ro;
  // SHEET part group in SHEET_PRICE mode: the parent row displays its OWN sheet
  // line price (parentPriceAmount + its GST) instead of the children rollup sum.
  // Display-only — the document totals still bill part pieces + sheet price.
  const sheetPriceShown = isParent && tU === 'SHEET'
    && resolveSheetCostMode(row.nestingConfig) === 'SHEET_PRICE';
  const level = num(row.level);
  const tint = nestRowTint(row.type, level);
  // Rollup parents: rate + discount are derived (Σ children) → read-only.
  // Dims / qty / gst stay editable so LAMI/DGU unit sizing + SHEET own size work.
  const rateRO = isParent;
  // Fabrication stays read-only on plain roll-up parents (SHEET / COMBO) but is
  // EDITABLE on a LAMI / DGU / LAMI+DGU assembly parent: a hole / cut-out drilled
  // through the unit applies to every glass layer, so the charge is billed
  // rate × count × qty × number of glass panes (computed in applyTreeRollup).
  const fabRO = isParent && !isLamiOrDgu(row.type);

  function cellBody(col) {
    const navIdx = navKeys.indexOf(col.key);
    const cellId = navIdx >= 0 ? `pfc-${vi}-${navIdx}` : undefined;
    switch (col.kind) {
      case 'serial':
        // Tree view: a PARENT row (LAMI / DGU / LAMI+DGU / SHEET / COMBO assembly)
        // shows an expand/collapse caret so its child layers can be folded away;
        // a leaf row just shows its serial number. Child rows are indented by
        // nesting level so the tree structure reads at a glance.
        if (isParent) {
          const expanded = row.isExpanded !== false;
          return (
            <div className="pfg-serial pfg-serial--parent" style={{ paddingLeft: level * 10 }}>
              <button type="button" className="pfg-tree-toggle"
                title={expanded ? 'Collapse layers' : 'Expand layers'}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                aria-expanded={expanded}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); if (onToggle) onToggle(row.refUuid); }}>
                {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
              </button>
              <span className="pfg-serial-no">{row.rowNo}</span>
            </div>
          );
        }
        return (
          <div className="pfg-serial" style={{ paddingLeft: level > 0 ? level * 10 + 14 : 0 }}>
            {row.rowNo}
          </div>
        );
      case 'select':
        return (
          <div className="pfg-act">
            <Checkbox checked={!!selected} disabled={ro}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => onToggleSelect(row.refUuid, e.target.checked)} />
          </div>
        );
      case 'action':
        return (
          <div className="pfg-act">
            <Button size="small" type="text" danger disabled={ro || linesLen <= 1}
              icon={<DeleteOutlined />} onClick={() => removeRow(row.refUuid)} />
          </div>
        );
      case 'text':
        return <Input id={cellId} value={row[col.field] || ''} disabled={ro} autoComplete="off"
          onChange={(e) => updateRow(row.refUuid, { [col.field]: e.target.value })} />;
      case 'glasstype':
        // A LAMI/DGU/LAMI+DGU parent shows its temper-derived assembly glass-type
        // (A-LAMI / T-LAMI / T+A+LAMI …) read-only; the structural type stays in
        // `row.type`. Editing it on the parent makes no sense — it's a roll-up.
        if (isParent && isLamiOrDgu(row.type)) {
          const lbl = (view && view.glassTypeCode) || row.glassTypeCode || row.type;
          return <div className="pfg-ro" title="Glass type derived from the laminate / DGU layers">{lbl}</div>;
        }
        return <TypeCell cellId={cellId} value={row[col.field]} glassTypes={dropdowns.glassTypes} readOnly={roType}
          onCommit={(v) => updateRow(row.refUuid, billLine
            // BILL line: re-label the glass type only — the piece keeps its cut
            // size/qty (frozen server-side), so never reset dimensions here.
            ? { [col.field]: v, glassTypeCode: isPieceType(v) ? null : resolveGlassTypeCode(v, dropdowns.glassTypes) }
            : (isPieceType(v)
              ? { [col.field]: v, glassTypeCode: null, actualWidthMm: 0, actualHeightMm: 0, manualChargeWidthMm: 0, manualChargeHeightMm: 0 }
              : { [col.field]: v, glassTypeCode: resolveGlassTypeCode(v, dropdowns.glassTypes) }))} />;
      case 'item': {
        // Hover tooltip shows the FULL item description (the cell is narrow and
        // clips long text). Prefer the composed display text, fall back to the
        // raw "#process" string the user typed.
        const fullDesc = (row.itemDesc && String(row.itemDesc))
          || (row.rawItemDesc && String(row.rawItemDesc)) || '';
        // Jumbo-size annotation (display-only): show "( JUMBO SIZE )" when the line
        // is over-size per the tenant's Jumbo Other-Charge condition. Not part of
        // the editable description; affects no value / GST / charge. Glass lines
        // only — never SHEET stock / GASFILL / hardware pieces.
        const isJumbo = !!jumboCondition
          && !isPieceType(row.type)
          && typeUp(row.type) !== 'SHEET' && typeUp(row.type) !== 'GASFILL'
          && matchJumboCondition(jumboCondition, view || row);
        return (
          <span title={fullDesc || undefined} style={{ display: 'flex', alignItems: 'center', width: '100%', paddingLeft: level * 14 }}>
            {isParent ? <span className="pfg-pmark">P</span> : null}
            {row.nestingConfig && row.nestingConfig.comboComponent && row.nestingConfig.comboComponent.optional
              ? <span title="Optional combo component" style={{ fontSize: 9, fontWeight: 700, color: '#0e7490', background: '#cffafe', borderRadius: 3, padding: '0 3px', marginRight: 4, flex: '0 0 auto', lineHeight: '14px' }}>OPT</span>
              : null}
            <SmartItemDescCell cellId={cellId} row={row} dropdowns={dropdowns} readOnly={roItem} onRftAdded={onRftAdded}
              onCommit={(patch) => updateRow(row.refUuid, patch)} onPickCombo={(it) => pickCombo(row.refUuid, it)} onPickItem={(it) => {
                pickItem(row.refUuid, it);
                // Hardware / service line: the W/H cells are blank, so jump focus
                // straight to QTY (the only thing the user types) after the pick.
                if (isPieceType(row.type)) {
                  const qIdx = navKeys.indexOf('qty');
                  if (qIdx >= 0) {
                    const qId = `pfc-${vi}-${qIdx}`;
                    setTimeout(() => {
                      const qEl = document.getElementById(qId);
                      if (qEl) { try { qEl.focus(); } catch (_e) { /* noop */ } if (qEl.select) qEl.select(); }
                    }, 0);
                  }
                }
              }} />
            {isJumbo ? (
              <span
                title="Jumbo size — flagged from the Jumbo Other-Charge size rule. Shown on the proforma print; does not change any amount."
                style={{ flex: '0 0 auto', marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: '.02em', color: '#b45309', background: '#fff7e6', border: '1px solid #fde3b3', borderRadius: 3, padding: '0 4px', lineHeight: '15px', whiteSpace: 'nowrap' }}>
                ( JUMBO SIZE )
              </span>
            ) : null}
            {/* WO-3.3 — Change-Order fate tag (display-only, like the Jumbo tag). */}
            {(struckCO || cancelledCO) ? (
              <span
                title={struckCO
                  ? 'Struck-out original — cancelled by a Change Order (locked, ₹0)'
                  : `Cancelled cut-piece line (${row.cancelDisp || ''}) — locked; billed per its fate`}
                style={{ flex: '0 0 auto', marginLeft: 6, fontSize: 9, fontWeight: 800, letterSpacing: '.03em', color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 3, padding: '0 4px', lineHeight: '15px', whiteSpace: 'nowrap' }}>
                {struckCO
                  ? '⊘ CANCELLED — REVISED'
                  : `⊘ ${row.cancelDisp === 'scrap' ? 'SCRAP' : row.cancelDisp === 'bill' ? 'BILL CUSTOMER' : 'OFF-CUT'}`}
              </span>
            ) : null}
          </span>
        );
      }
      case 'dim': {
        // HARDWARE / SERVICES are per-piece lines with no dimensions — keep the
        // W/H cells blank and uneditable so size can't be entered by mistake.
        // Keep the cell id + make it programmatically focusable so keyboard
        // navigation still flows THROUGH the cell (otherwise getElementById
        // returns null and focus can't advance past ITEM DESC).
        if (isPieceType(row.type)) return <div id={cellId} tabIndex={-1} className="pfg-ro" />;
        const mm = num(row[col.mmField]);
        // For the unlocked chargeable cells, show the auto (wastage) charge size
        // as a normal value when nothing is typed yet — the regular-workflow value
        // stays visible (not dimmed) and only a typed value becomes an override.
        const phKey = col.mmField === 'manualChargeWidthMm' ? 'chargeWidthMm'
          : col.mmField === 'manualChargeHeightMm' ? 'chargeHeightMm' : null;
        const phMm = phKey ? num(view[phKey]) : 0;
        return <DimCell cellId={cellId} mm={mm} unit={col.unit} readOnly={ro} placeholderMm={phMm}
          onCommit={(valMm) => updateRow(row.refUuid, { [col.mmField]: valMm })} />;
      }
      case 'calcdim': {
        const mm = num(view[col.viewKey]);
        const disp = mm > 0 ? displayDimensionInUnit(mm, col.unit) : 0;
        // Inches display as nearest-1/16 fractions (matching the editable DimCell),
        // not decimals; MM/CM/FT keep the decimal formatter.
        return <div className="pfg-ro">{mm > 0 ? (col.unit === 'IN' ? decimalToFraction16(disp) : fmtDim(disp, col.unit)) : ''}</div>;
      }
      case 'num':
        // Manual PARENT price: the PRICE (salesRate) cell on a roll-up parent is
        // editable and bound to nestingConfig.parentRate — billed rate × the
        // parent's own area on TOP of the child sum. The derived combination /
        // sheet rate shows as a faint placeholder.
        // Manual PARENT price entry: BOTH PRICE columns (primary salesRate +
        // alternate alternateRate) on a roll-up parent bind to nestingConfig.parentRate
        // so the sheet line can be priced from whichever price cell the user clicks.
        if (isParent && (col.field === 'salesRate' || col.field === 'alternateRate')) {
          const cfg = row.nestingConfig || {};
          const derived = num(view[col.field] != null ? view[col.field] : row[col.field]);
          // A SHEET part group prices the sheet line ONLY in SHEET_PRICE mode; in
          // WASTAGE mode (the default) the cell is locked and the sheet bills its
          // scrap instead. Non-SHEET assemblies (LAMI/DGU) are always priceable.
          const sheetMode = tU === 'SHEET' ? resolveSheetCostMode(cfg) : 'SHEET_PRICE';
          const priceLocked = tU === 'SHEET' && sheetMode !== 'SHEET_PRICE';
          return <InputNumber id={cellId} className="w-full" min={0} controls={false}
            disabled={ro || priceLocked}
            value={cfg.parentRate != null && cfg.parentRate !== 0 ? cfg.parentRate : null}
            placeholder={priceLocked ? 'wastage' : (derived ? derived.toFixed(2) : '+ price')}
            title={priceLocked ? 'Switch this sheet to “Sheet price” mode (in the NESTING bar) to price the sheet line.' : undefined}
            onKeyDown={guardDecimal} onPaste={pasteDecimal}
            onChange={(val) => updateRow(row.refUuid, { nestingConfig: { ...cfg, parentRate: val || 0 } })} />;
        }
        if (rateRO) {
          const rv = num(view[col.field] != null ? view[col.field] : row[col.field]);
          return <div className="pfg-ro">{rv ? rv.toFixed(col.precision || 2) : ''}</div>;
        }
        return <InputNumber id={cellId} className="w-full" min={0} controls={false}
          value={(col.field === 'lineDiscount'
            // Show the %-derived amount while a % is set (read display); with no %
            // the flat amount is edited directly from the raw value (no snap).
            ? (num(row.discPct) > 0 ? num(view.lineDiscount) : num(row.lineDiscount))
            : num(row[col.field])) || null}
          disabled={billLine && (col.field === 'salesRate' || col.field === 'alternateRate') ? roIn : ro}
          precision={col.forceDecimals ? col.precision : undefined}
          onKeyDown={guardDecimal} onPaste={pasteDecimal}
          onChange={(val) => updateRow(row.refUuid,
            // Overriding either PRICE rate auto-derives the sibling UOM rate
            // (and the line amount recomputes via computeTreeRows).
            (col.field === 'salesRate' || col.field === 'alternateRate')
              ? {
                ...syncRatePair(col.field, val || 0, priceUomCode),
                // Stage 6PL-1c — a hand-typed rate is the operator's own
                // decision. Stamping it MANUAL is what makes it immune to any
                // later automatic re-pricing (migration 0182's whole purpose):
                // "re-price 3 lines" must never silently overwrite a number a
                // human chose.
                priceSource: 'MANUAL', priceListId: null
              }
              // Discount % and flat AMT are mutually exclusive — entering one
              // clears the other, so the engine's "% drives, else flat amount"
              // rule never silently overrides a typed amount (2026-08-18).
              : col.field === 'lineDiscount'
                ? { lineDiscount: val || 0, discPct: 0 }
                : col.field === 'discPct'
                  ? { discPct: val || 0, lineDiscount: 0 }
                  : { [col.field]: val || 0 })} />;
      case 'int':
        return <InputNumber id={cellId} className="w-full" min={0} precision={0} controls={false} value={row[col.field] || null} disabled={ro}
          onKeyDown={guardInteger} onPaste={pasteInteger}
          onChange={(val) => updateRow(row.refUuid, { [col.field]: val || 0 })} />;
      case 'fab': {
        if (fabRO) return <div className="pfg-ro" />;
        // HARDWARE / SERVICES carry no fabrication — lock the cell blank, but keep
        // the cell id + make it focusable so keyboard navigation still flows.
        if (isPieceType(row.type)) return <div id={cellId} tabIndex={-1} className="pfg-ro" />;
        // A glass-pane child of a LAMI/DGU shows the parent's fabrication MIRRORED
        // (read-only): the hole/cut-out drilled through the unit passes through every
        // pane, so the tree reflects it on each layer. Billing stays on the parent —
        // the child input is disabled so it can't be edited (no double-count).
        const mirrored = view && Array.isArray(view.mirroredFabEntries) ? view.mirroredFabEntries : null;
        if (mirrored) {
          const me = mirrored.find((e) => e && e.code === col.fabCode);
          const mc = me ? num(me.count) : 0;
          return (
            <div id={cellId} tabIndex={-1}
              className={`pfg-ro pfg-fab-mirror${mc > 0 ? ' has' : ''}`}
              title={mc > 0 ? 'From the assembly line (applies to every glass layer)' : ''}>
              {mc > 0 ? mc : ''}
            </div>
          );
        }
        return <InputNumber id={cellId} className="w-full" min={0} precision={0} controls={false} value={getFabCount(row, col.fabCode) || null} disabled={roFab}
          onKeyDown={guardInteger} onPaste={pasteInteger}
          onChange={(val) => updateRow(row.refUuid, { fabricationDetails: setFabCount(row, col.fabCode, col.fabRate, val || 0) })} />;
      }
      case 'gst':
        return <Select value={Number(row.gstRate)} disabled={ro} options={gstOptions}
          onChange={(val) => updateRow(row.refUuid, { gstRate: val })} />;
      case 'calc': {
        const cv = num(view[col.viewKey]);
        return <div className="pfg-ro">{cv ? cv.toFixed(col.precision || 0) : ''}</div>;
      }
      case 'money': {
        const mcls = col.key === 'otherChargeAmount' ? ' oc' : (col.key === 'basicAmount' ? ' basic' : '');
        // A SHEET part group in SHEET_PRICE mode shows its OWN sheet line price in
        // the amount cell (rate × sheet area × qty), not the children's rollup sum
        // — the sheet is billed like any other line item. Part pieces keep their own
        // amounts and the document total still bills both (display-only override).
        if (sheetPriceShown && (col.key === 'basicAmount' || col.key === 'gstAmount')) {
          const ov = col.key === 'basicAmount' ? num(view.parentPriceAmount) : num(view.parentPriceGst);
          return <div className={`pfg-ro${mcls}`}>{ov ? gridMoney(ov) : ''}</div>;
        }
        const mv = num(col.key === 'otherChargeAmount' ? (view.otherChargeAmount != null ? view.otherChargeAmount : row.otherChargeAmount) : view[col.key]);
        return <div className={`pfg-ro${mcls}`}>{mv ? gridMoney(mv) : ''}</div>;
      }
      case 'total': {
        // SHEET_PRICE: the SHEET row's total is its own sheet line total
        // (sheet price + that price's GST), not the children rollup.
        if (sheetPriceShown) {
          const own = num(view.parentPriceAmount) + num(view.parentPriceGst);
          return <div className="pfg-ro tot">{own ? gridMoney(own) : ''}</div>;
        }
        return <div className="pfg-ro tot">{num(view.lineTotal) ? gridMoney(view.lineTotal) : ''}</div>;
      }
      default:
        return null;
    }
  }

  return (
    <div data-ref={row.refUuid} className={`pfg-row${isParent ? ' pfg-parent' : ''}${struckCO ? ' pfg-struck' : ''}${cancelledCO ? ' pfg-cancelled' : ''}`} style={{ width: totalWidth, height: ROW_H, background: tint || undefined }}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu(e.clientX, e.clientY, row.refUuid); }}>
      {columns.map((col) => {
        const isFL = col.fixed === 'left'; const isFR = col.fixed === 'right';
        const cls = ['pfg-cell'];
        if (col.bg) cls.push(col.bg);
        if (col.key === 'itemDesc' || col.key === 'type') cls.push('pfg-col-item');
        if (isFL) cls.push('pfg-fixed');
        if (isFL && col.key === lastFixedLeftKey) cls.push('pfg-fixed-last');
        if (isFR) cls.push('pfg-fixed-r');
        const inFill = fillHi && col.key === fillHiCol;
        if (inFill) cls.push('pfg-fillhi');
        const style = {
          width: col.width,
          ...(tint ? { background: tint } : {}),
          ...(isFL ? { position: 'sticky', left: colLeft[col.key] } : {}),
          ...(isFR ? { position: 'sticky', right: (colRight && colRight[col.key]) || 0 } : {})
        };
        const fillable = !ro && !isParent && FILL_FIELDS[col.key];
        // A LAMI/DGU/LAMI+DGU assembly PARENT gets a copy handle on ITEM DESC that
        // clones the whole unit block onto the rows below (assemblyDragCopyTo).
        const assemblyFill = !ro && isParent && col.key === 'itemDesc' && isLamiOrDgu(row.type);
        const wholeRow = fillable && FILL_FIELDS[col.key].includes(FILL_WHOLE_ROW);
        const fillTitle = assemblyFill
          ? `Drag down to copy this ${tU} unit onto the rows below`
          : (col.key === 'itemDesc' ? 'Drag down to copy the item description only'
            : (wholeRow ? 'Drag down to copy the WHOLE row' : 'Drag down to copy'));
        return (
          <div key={col.key} className={cls.join(' ')} style={style}>
            {cellBody(col)}
            {(fillable || assemblyFill) ? (
              <span className="pfg-fill" title={fillTitle}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startFillDrag(row.refUuid, col.key, assemblyFill ? 'assembly' : 'cell'); }} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
});

export default function ProformaLineGrid({ lines, setLines, header, dropdowns, readOnly, pricedRows }) {
  const scrollRef = useRef(null);
  const ro = !!readOnly;

  // ── ITEM DESC column resize ─────────────────────────────────────────
  // Drag the ITEM DESC header's right edge to widen/narrow it; double-click the
  // handle to reset to the default. Persisted per browser so it survives reloads.
  const [itemDescWidth, setItemDescWidth] = useState(() => {
    try { const v = parseInt(window.localStorage.getItem('pfg.itemDescWidth'), 10); return v > 0 ? v : null; }
    catch (_e) { return null; }
  });
  const startColResize = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const cell = e.currentTarget.parentElement;
    const startW = cell ? Math.round(cell.getBoundingClientRect().width) : 320;
    const prevSel = document.body.style.userSelect;
    const prevCur = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    const move = (ev) => {
      const w = Math.max(160, Math.min(900, startW + (ev.clientX - startX)));
      setItemDescWidth(w);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.userSelect = prevSel;
      document.body.style.cursor = prevCur;
      setItemDescWidth((w) => { try { if (w > 0) window.localStorage.setItem('pfg.itemDescWidth', String(w)); } catch (_e) { /* noop */ } return w; });
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, []);
  const resetColResize = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setItemDescWidth(null);
    try { window.localStorage.removeItem('pfg.itemDescWidth'); } catch (_e) { /* noop */ }
  }, []);

  // ── tree mutation helpers (operate by refUuid; rebuildTree after) ──
  const updateRow = useCallback((refUuid, patch) => {
    setLines((rows) => {
      const idx = rows.findIndex((r) => r.refUuid === refUuid);
      if (idx < 0) return rows;
      const cur = rows[idx];
      // `patch` may be a function of the current row (used when a field's new
      // value depends on what the row already holds — e.g. an item pick that must
      // not clobber a TYPE-derived glassTypeCode).
      const resolved = typeof patch === 'function' ? patch(cur) : patch;
      const next = rows.slice();
      next[idx] = { ...cur, ...resolved };
      // Task-2 — typing an ACTUAL size on a normal line stamps the current Wastage-
      // Mode tolerance onto THAT row's chargeable size (its manual charge override),
      // so wastage is applied per row as it's entered. Untouched rows keep billing
      // on ACTUAL (Task-1). Pieces + roll-up parents carry no chargeable dims → skip.
      if (!isPieceType(next[idx].type) && !next[idx].hasChildren) {
        if ('actualWidthMm' in resolved) {
          next[idx].manualChargeWidthMm = chargeStampForActual(next[idx].actualWidthMm, header.wastageMode, header.wastageWidthMm);
        }
        if ('actualHeightMm' in resolved) {
          next[idx].manualChargeHeightMm = chargeStampForActual(next[idx].actualHeightMm, header.wastageMode, header.wastageHeightMm);
        }
      }
      // LAMI/DGU parent: dims + qty sync DOWN to direct children. A LAMI/DGU bills
      // as Σ(child plies), and the plies are sized via THIS sync (not a direct
      // ACTUAL edit), so the top-of-function auto-stamp never reaches them — that
      // is why an assembly used to bill on ACTUAL, ignoring Wastage Mode. Stamp the
      // Wastage-Mode CHARGEABLE size onto the parent's own display dims AND every
      // direct child, so the assembly bills on the chargeable area exactly like a
      // normal glass line. chargeStampForActual returns 0 when no tolerance applies,
      // so the row then falls back to ACTUAL — no change when no wastage is set. A
      // later manual CHRG edit on a ply still wins until the next parent re-size
      // (mirrors normal-line ACTUAL entry re-stamping its own row).
      if (next[idx].hasChildren && isLamiOrDgu(next[idx].type)) {
        const dimKeys = ['actualWidthMm', 'actualHeightMm'];
        const touched = Object.keys(resolved).some((k) => dimKeys.includes(k) || k === 'qty');
        const mcTouched = ('manualChargeWidthMm' in resolved) || ('manualChargeHeightMm' in resolved);
        // Sync flows to the WHOLE subtree, not just direct children: a LAMI+DGU has a
        // nested LAMI pane whose glass plies sit one level deeper (their parentRefUuid
        // is the LAMI sub-parent, not this DGU). Those plies carry the money, so if the
        // sync stopped at direct children they never got the panel size — area 0 → they
        // billed ₹0 and the unit collapsed to just its spacer. Pre-order guarantees each
        // parent precedes its children, so one forward pass builds the descendant set.
        const subtree = new Set([refUuid]);
        for (let j = 0; j < next.length; j += 1) {
          const pr = next[j].parentRefUuid;
          if (pr && subtree.has(pr)) subtree.add(next[j].refUuid);
        }
        const inSubtree = (r) => r.parentRefUuid != null && subtree.has(r.parentRefUuid);
        if (touched) {
          const stampW = chargeStampForActual(next[idx].actualWidthMm, header.wastageMode, header.wastageWidthMm);
          const stampH = chargeStampForActual(next[idx].actualHeightMm, header.wastageMode, header.wastageHeightMm);
          if ('actualWidthMm' in resolved) next[idx].manualChargeWidthMm = stampW;
          if ('actualHeightMm' in resolved) next[idx].manualChargeHeightMm = stampH;
          for (let j = 0; j < next.length; j += 1) {
            if (inSubtree(next[j])) {
              next[j] = {
                ...next[j],
                actualWidthMm: next[idx].actualWidthMm,
                actualHeightMm: next[idx].actualHeightMm,
                qty: next[idx].qty,
                ...('actualWidthMm' in resolved ? { manualChargeWidthMm: stampW } : {}),
                ...('actualHeightMm' in resolved ? { manualChargeHeightMm: stampH } : {})
              };
            }
          }
        } else if (mcTouched) {
          // A manual CHRG edit OR the Shift+↓/↑ wastage brush landing on a LAMI/DGU
          // PARENT (no ACTUAL change) flows the SAME chargeable size down to every
          // descendant ply, so the billed Σ(plies) matches the parent's shown chargeable.
          // This is what makes the wastage brush work on assembly rows (their plies
          // carry the money; stamping only the parent would change display, not billing).
          for (let j = 0; j < next.length; j += 1) {
            if (inSubtree(next[j])) {
              next[j] = {
                ...next[j],
                ...('manualChargeWidthMm' in resolved ? { manualChargeWidthMm: resolved.manualChargeWidthMm } : {}),
                ...('manualChargeHeightMm' in resolved ? { manualChargeHeightMm: resolved.manualChargeHeightMm } : {})
              };
            }
          }
        }
      }
      return rebuildTree(next);
    });
  }, [setLines, header.wastageMode, header.wastageWidthMm, header.wastageHeightMm]);

  // NOTE — changing the Wastage Mode / value does NOT touch any existing row (by
  // design, per the user): the setting only affects rows entered/brushed afterward.
  // Wastage reaches a row via (a) typing its ACTUAL size (stamped in updateRow) or
  // (b) the Shift+↓/↑ brush in the ACTUAL column (see onKeyDown). There is no
  // reactive re-stamp on a settings change.

  const removeRow = useCallback((refUuid) => {
    setLines((rows) => {
      if (rows.length <= 1) return rows;
      const idx = rows.findIndex((r) => r.refUuid === refUuid);
      if (idx < 0) return rows;
      const end = descEnd(rows, idx);            // remove the whole subtree
      const next = rows.slice(0, idx).concat(rows.slice(end));
      return rebuildTree(next.length ? next : [blankLine(1)]);
    });
  }, [setLines]);

  const addRow = useCallback(() => {
    setLines((rows) => (rows.length >= MAX_LINES ? rows : rebuildTree(rows.concat([blankLine(rows.length + 1)]))));
  }, [setLines]);

  // Keep every SHEET master's stock-size children grouped directly below it
  // (sheet sizes first, then any other children). Fixes legacy ordering on load
  // and re-tidies after each edit. No-op once grouped → no render loop.
  useEffect(() => {
    if (ro) return;
    setLines((rows) => {
      const g = groupSheetChildren(rows);
      if (g === rows || (g.length === rows.length && g.every((r, i) => r === rows[i]))) return rows;
      return rebuildTree(g);
    });
  }, [lines, ro, setLines]);

  const insertRow = useCallback((refUuid, where) => {
    setLines((rows) => (rows.length >= MAX_LINES ? rows : insertRelative(rows, refUuid, where)));
  }, [setLines]);

  // Insert N blank rows relative to a target (same-level siblings). Each is added
  // via insertRelative so SHEET-master / nesting placement rules are honoured, and
  // the total is capped at MAX_LINES (a partial insert + a notice if the cap is
  // hit). "below" inserts so the rows end up in the order requested.
  const insertRows = useCallback((refUuid, where, count) => {
    const n = Math.max(1, Math.min(MAX_LINES, Math.trunc(Number(count) || 0)));
    setLines((rows) => {
      let next = rows;
      let added = 0;
      // For "below" insert each new row relative to the PREVIOUS new row so they
      // stay in order; for "above" insert all relative to the original target.
      let anchor = refUuid;
      for (let k = 0; k < n; k += 1) {
        if (next.length >= MAX_LINES) break;
        const before = next.length;
        next = insertRelative(next, anchor, where);
        if (next.length === before) break;            // nothing inserted (bad ref)
        added += 1;
        if (where === 'below') {
          // The newly inserted sibling sits right after the anchor's subtree; make
          // it the next anchor so row k+1 lands after it (preserves entry order).
          const ai = next.findIndex((r) => r && r.refUuid === anchor);
          const at = ai >= 0 ? descEnd(next, ai) : -1;
          if (at >= 0 && at < next.length) anchor = next[at].refUuid;
        }
      }
      if (added < n) {
        message.warning(`Added ${added} of ${n} rows — the ${MAX_LINES}-line limit was reached.`);
      }
      return next;
    });
  }, [setLines]);

  const insertChild = useCallback((refUuid, childType) => {
    setLines((rows) => {
      if (rows.length >= MAX_LINES) return rows;
      const idx = rows.findIndex((r) => r.refUuid === refUuid);
      if (idx < 0) return rows;
      const parent = rows[idx];
      const nr = blankLine(1, num(parent.level) + 1, parent.refUuid);
      nr.location = parent.location || '';
      if (childType) nr.type = childType;
      if (childType === LINE_TYPE.SHEET) nr.itemDesc = parent.itemDesc || '';
      const at = descEnd(rows, idx);             // append after existing children
      const next = rows.slice();
      next[idx] = { ...parent, isExpanded: true };
      next.splice(at, 0, nr);
      return rebuildTree(next);
    });
  }, [setLines]);

  const toggleExpand = useCallback((refUuid) => {
    setLines((rows) => rows.map((r) => (r.refUuid === refUuid ? { ...r, isExpanded: r.isExpanded === false } : r)));
  }, [setLines]);

  const setRowType = useCallback((refUuid, type) => {
    updateRow(refUuid, { type });
  }, [updateRow]);

  // Replace a parent's whole child subtree with a generated block (LAMI/DGU
  // popups). `block` is a pre-order array of descendant rows with level +
  // parentRefUuid already set relative to the parent.
  const replaceSubtree = useCallback((refUuid, parentPatch, block) => {
    setLines((rows) => {
      const idx = rows.findIndex((r) => r.refUuid === refUuid);
      if (idx < 0) return rows;
      const parent = rows[idx];
      const end = descEnd(rows, idx);
      // isExpanded defaults to true but the patch may override it (LAMI/DGU
      // collapse their layer children so the grid shows one consolidated row).
      const mergedParent = { ...parent, isExpanded: true, ...parentPatch };
      let parentOut = mergedParent;
      let blockOut = Array.isArray(block) ? block : [];
      // A freshly generated LAMI/DGU assembly bills as Σ(child plies) sized from the
      // parent's panel size. Stamp the Wastage-Mode CHARGEABLE size onto the parent's
      // own display dims and every generated descendant so the assembly bills on the
      // chargeable area (not ACTUAL) — same rule as the parent→child re-size sync in
      // updateRow, applied here for the config-popup generation path. chargeStampFor
      // Actual returns 0 (→ ACTUAL fallback) when no wastage tolerance is configured,
      // so this is a no-op when Wastage Mode adds nothing.
      if (isLamiOrDgu(mergedParent.type)) {
        const stampRow = (r) => ({
          ...r,
          manualChargeWidthMm: chargeStampForActual(num(r.actualWidthMm), header.wastageMode, header.wastageWidthMm),
          manualChargeHeightMm: chargeStampForActual(num(r.actualHeightMm), header.wastageMode, header.wastageHeightMm)
        });
        parentOut = stampRow(mergedParent);
        blockOut = blockOut.map(stampRow);
      }
      const next = rows.slice(0, idx)
        .concat([parentOut])
        .concat(blockOut)
        .concat(rows.slice(end));
      return rebuildTree(next);
    });
  }, [setLines, header.wastageMode, header.wastageWidthMm, header.wastageHeightMm]);

  // Replicate a LAMI/DGU cut-piece unit onto the `count` cut-piece rows below it
  // within the same SHEET group. Each target row (and its whole subtree) is
  // replaced with a fresh deep-clone of the source unit — an EXACT duplicate
  // (layers, sizes, qty, fabrication, rates) with new refUuids, re-parented to
  // the same sheet master. Purely structural + additive; the nesting engine
  // recomputes cut area from the new pieces exactly as it does for hand-built
  // ones. Stock sheet-size rows and rows outside the group are never touched.
  const copyUnitDown = useCallback((sourceRefUuid, count) => {
    setLines((rows) => {
      const plan = planUnitCopy(rows, sourceRefUuid);
      if (!plan) return rows;
      const n = Math.max(0, Math.min(Number(count) || 0, plan.targets.length));
      if (n === 0) return rows;
      const sourceBlock = rows.slice(plan.sIdx, plan.sourceEnd);
      const replaceAt = new Map(plan.targets.slice(0, n).map((t) => [t.idx, t.end]));
      const out = [];
      let i = 0;
      while (i < rows.length) {
        if (replaceAt.has(i)) {
          out.push(...cloneUnitBlock(sourceBlock, plan.masterRef));
          i = replaceAt.get(i);
        } else {
          out.push(rows[i]);
          i += 1;
        }
      }
      return rebuildTree(out);
    });
  }, [setLines]);

  // Drag-handle version of the LAMI/DGU/LAMI+DGU unit copy: replace the sibling
  // "unit" rows in the dragged range (bounded by the drop row) with an exact
  // clone of the source assembly block. Works top-level and inside sheet nesting
  // (see planAssemblyDragCopy). Each target keeps its own parent link; rebuildTree
  // recomputes rowNo/hasChildren so the copies recalculate like hand-built ones.
  const assemblyDragCopyTo = useCallback((sourceRefUuid, dropRefUuid) => {
    setLines((rows) => {
      const plan = planAssemblyDragCopy(rows, sourceRefUuid, dropRefUuid);
      if (!plan) return rows;
      const sourceBlock = rows.slice(plan.sIdx, plan.sourceEnd);
      const replaceAt = new Map(plan.targets.map((t) => [t.idx, t.end]));
      const out = [];
      let i = 0;
      while (i < rows.length) {
        if (replaceAt.has(i)) {
          out.push(...cloneUnitBlock(sourceBlock, rows[i].parentRefUuid || null));
          i = replaceAt.get(i);
        } else {
          out.push(rows[i]);
          i += 1;
        }
      }
      return rebuildTree(out);
    });
  }, [setLines]);

  // Menu-driven "copy the row WITH its details" — the non-drag alternative to the
  // fill-handle drag-copy. Clones the source row (and, if it is a LAMI/DGU/SHEET/
  // COMBO parent, its whole subtree) `count` times, inserting the copies directly
  // below the source at the SAME level/parent. Every field is deep-cloned with
  // fresh refUuids (via cloneUnitBlock), so each copy carries item, dimensions,
  // qty, rates, glass spec, fabrication, nesting layers + remarks and prices
  // exactly like the original. Honours the MAX_LINES cap (partial copy + notice).
  const duplicateRow = useCallback((sourceRefUuid, count) => {
    const n = Math.max(1, Math.min(MAX_LINES, Math.trunc(Number(count) || 0)));
    setLines((rows) => {
      const idx = rows.findIndex((r) => r && r.refUuid === sourceRefUuid);
      if (idx < 0) return rows;
      const src = rows[idx];
      const end = descEnd(rows, idx);              // source + its whole subtree
      const block = rows.slice(idx, end);
      const parentRef = src.parentRefUuid || null; // copies are same-level siblings
      const room = Math.max(0, MAX_LINES - rows.length);
      const copies = Math.min(n, Math.floor(room / block.length));
      if (copies <= 0) {
        message.warning(`Adding a copy would exceed the ${MAX_LINES}-line limit.`);
        return rows;
      }
      const clones = [];
      for (let k = 0; k < copies; k += 1) clones.push(...cloneUnitBlock(block, parentRef));
      const next = rows.slice(0, end).concat(clones).concat(rows.slice(end));
      if (copies < n) message.warning(`Copied ${copies} of ${n} — the ${MAX_LINES}-line limit was reached.`);
      return rebuildTree(groupSheetChildren(next));
    });
  }, [setLines]);


  // Excel-style drag-fill: copy this cell's value(s) down to the row the cursor
  // is released on (inclusive). Roll-up parents are always skipped. A leaf is
  // written when it is TOP-LEVEL or sits inside a SHEET part-nesting group — the
  // sheet's cut-piece children are real billable rows, so drag-copy / whole-row
  // copy must reach them. LAMI/DGU combo layer-children stay excluded (their
  // build-up is not user-filled). A fresh patch is built per target so each gets
  // its OWN deep-cloned object fields; identity/parent links are preserved.
  const fillRangeTo = useCallback((srcRefUuid, fields, targetRefUuid) => {
    setLines((rows) => {
      const idx = rows.findIndex((r) => r.refUuid === srcRefUuid);
      const tIdx = rows.findIndex((r) => r.refUuid === targetRefUuid);
      if (idx < 0 || tIdx <= idx) return rows;
      const src = rows[idx];
      // True when a nested row lives under a SHEET ancestor (cut-piece children).
      const byRef = new Map(rows.map((r) => [r.refUuid, r]));
      const inSheetGroup = (r) => {
        let cur = r; const seen = new Set();
        while (cur && cur.parentRefUuid && !seen.has(cur.refUuid)) {
          seen.add(cur.refUuid);
          const p = byRef.get(cur.parentRefUuid);
          if (!p) return false;
          if (typeUp(p.type) === 'SHEET') return true;
          cur = p;
        }
        return false;
      };
      const writable = (r) => !r.hasChildren && (!r.parentRefUuid || inSheetGroup(r));
      return rows.map((r, i) => (i > idx && i <= tIdx && writable(r)
        ? { ...r, ...buildFillPatch(src, fields) } : r));
    });
  }, [setLines]);

  // Fill-down (keyboard): copy the given fields from one row into another (the
  // row above into the current one). Used by the keyboard fill-down shortcut.
  const fillCellFromRow = useCallback((srcRefUuid, targetRefUuid, fields) => {
    if (!srcRefUuid || !targetRefUuid || !fields) return;
    setLines((rows) => {
      const src = rows.find((r) => r.refUuid === srcRefUuid);
      if (!src) return rows;
      return rows.map((r) => (r.refUuid === targetRefUuid ? { ...r, ...buildFillPatch(src, fields) } : r));
    });
  }, [setLines]);

  // After a row mutation (e.g. delete) the DOM cell that had focus is gone, so the
  // browser drops focus to <body> and keyboard nav stalls. Re-land focus on the
  // same visible slot/column once React has re-rendered: the row that shifted up
  // now sits there; if we deleted the last row, walk up to the nearest cell.
  const focusVisCell = useCallback((vIdx, cIdx) => {
    const land = () => {
      let r = vIdx; let el = null;
      while (r >= 0 && !(el = document.getElementById(`pfc-${r}-${cIdx}`))) r -= 1;
      if (el) {
        try { el.focus({ preventScroll: true }); } catch (_e) { el.focus(); }
        if (el.select) el.select();
      }
    };
    // Two frames: state flush → DOM paint, so the target cell exists when we focus.
    requestAnimationFrame(() => requestAnimationFrame(land));
  }, []);

  // Drag state lives in a ref (no re-render per mouse move); `fillDrag` mirrors
  // the current range into state only when the hovered row CHANGES, to paint the
  // highlight without thrashing the virtualized grid.
  const fillDragRef = useRef(null);
  const [fillDrag, setFillDrag] = useState(null);   // { srcRef, colKey, overRef }

  // Per-row selection (grid-local; not persisted with the proforma). Kept as a
  // Set of refUuids so toggling one row re-renders only that row (GridRow memo).
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const toggleSelect = useCallback((refUuid, checked) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (checked) next.add(refUuid); else next.delete(refUuid);
      return next;
    });
  }, []);
  // Select-all (header checkbox) over the current line rows.
  const allRefs = useMemo(() => lines.map((l) => l.refUuid).filter(Boolean), [lines]);
  const selCount = useMemo(() => allRefs.reduce((n, r) => n + (selectedRows.has(r) ? 1 : 0), 0), [allRefs, selectedRows]);
  const allChecked = allRefs.length > 0 && selCount === allRefs.length;
  const someChecked = selCount > 0 && selCount < allRefs.length;
  const toggleSelectAll = useCallback((checked) => {
    setSelectedRows(checked ? new Set(allRefs) : new Set());
  }, [allRefs]);
  const startFillDrag = useCallback((srcRefUuid, colKey, mode) => {
    const isAssembly = mode === 'assembly';
    // Assembly drag copies the WHOLE unit block (handled by assemblyDragCopyTo);
    // the field list is only used for the fillRangeTo (cell/whole-row) path.
    const fields = isAssembly ? [FILL_WHOLE_ROW] : FILL_FIELDS[colKey];
    if (!fields) return;
    fillDragRef.current = { srcRef: srcRefUuid, colKey, fields, mode: isAssembly ? 'assembly' : 'cell', over: srcRefUuid };
    setFillDrag({ srcRef: srcRefUuid, colKey, overRef: srcRefUuid, mode: isAssembly ? 'assembly' : 'cell' });
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const move = (e) => {
      // Auto-scroll when the cursor nears the top / bottom edge so a fill can
      // extend past the visible rows.
      const sc = scrollRef.current;
      if (sc) {
        const r = sc.getBoundingClientRect();
        if (e.clientY > r.bottom - 24) sc.scrollTop += 12;
        else if (e.clientY < r.top + HEADER_H + 12) sc.scrollTop -= 12;
      }
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const rowEl = el && el.closest ? el.closest('.pfg-row') : null;
      const ref = rowEl && rowEl.dataset ? rowEl.dataset.ref : null;
      if (ref && ref !== fillDragRef.current.over) {
        fillDragRef.current.over = ref;
        setFillDrag({ srcRef: fillDragRef.current.srcRef, colKey, overRef: ref });
      }
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.userSelect = prevUserSelect;
      const fd = fillDragRef.current;
      fillDragRef.current = null;
      setFillDrag(null);
      if (fd && fd.over && fd.over !== fd.srcRef) {
        if (fd.mode === 'assembly') assemblyDragCopyTo(fd.srcRef, fd.over);
        else fillRangeTo(fd.srcRef, fd.fields, fd.over);
      }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [fillRangeTo, assemblyDragCopyTo]);

  // ── context menu + popups ──
  const [contextMenu, setContextMenu] = useState(null);   // { x, y, refUuid }
  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [contextMenu]);
  const openContextMenu = useCallback((x, y, refUuid) => setContextMenu({ x, y, refUuid }), []);
  // Stable so the memoized GridRow doesn't churn — opens the RFT/RMT edge picker.
  const openEdgePicker = useCallback((refUuid) => setEdgeTarget(refUuid), []);
  const [lamiTarget, setLamiTarget] = useState(null);     // refUuid
  const [dguTarget, setDguTarget] = useState(null);       // { refUuid, lamiDgu }
  const [glassCfgTarget, setGlassCfgTarget] = useState(null);
  const [narrTarget, setNarrTarget] = useState(null);
  const [edgeTarget, setEdgeTarget] = useState(null);     // refUuid (RFT/RMT edge picker)
  // "Add multiple rows" prompt: { refUuid } when open + the entered count.
  const [addRowsTarget, setAddRowsTarget] = useState(null);
  const [addRowsCount, setAddRowsCount] = useState(5);
  // "Copy row with details" prompt: { refUuid, hasChildren } + the copy count.
  const [copyRowsTarget, setCopyRowsTarget] = useState(null);
  const [copyRowsCount, setCopyRowsCount] = useState(1);
  // "Copy unit down to…" prompt for a LAMI/DGU unit inside a sheet group.
  const [copyUnitTarget, setCopyUnitTarget] = useState(null);   // { refUuid, max }
  const [copyUnitCount, setCopyUnitCount] = useState(1);
  // Stage 6PL-1e — "Why this rate?" target row (refUuid). Read-only-safe: the
  // question is asked most often about a CONFIRMED document, which is exactly
  // when the rest of the context menu is hidden.
  const [rationaleTarget, setRationaleTarget] = useState(null);

  const pickItem = useCallback((refUuid, it) => {
    if (!it) return;
    // The item's sales rate is stored in its own (default) UOM. Convert it
    // into THIS document's Price UOM (from Proforma Settings) so the line
    // bills correctly; alternateRate carries the other area UOM as a
    // fallback. Min/Max are kept on the snapshot for reference only.
    const { salesRate, alternateRate } = resolveItemRateForDoc(
      it.salesRate, it.defaultUomCode, header.priceUomCode);
    const pickedThk = parseThicknessMm(it.itemDesc || it.itemName);
    updateRow(refUuid, (cur) => ({
      itemId: it.id, itemDesc: it.itemDesc || it.itemName || '',
      hsnSac: it.hsncode || null,
      gstRate: it.gstRatePercent != null ? Number(it.gstRatePercent) : 18,
      taxability: it.taxabilityType || null,
      // Glass thickness for the THICKNESS column + weight calc. Prefer the item's
      // own glass-spec thickness (authoritative); fall back to parsing the
      // description. 0 when absent.
      thicknessMm: it.thicknessMm != null ? Number(it.thicknessMm) : (pickedThk != null ? pickedThk : 0),
      salesRate,
      alternateRate,
      materialTypeCode: it.materialTypeCode || null,
      categoryCode: it.itemCategoryCode || null,
      // Glass type drives glass-type-scoped Other Charges (Shape / Template, …).
      // The TYPE column is the authoritative source — picking T-SHAPE / A-SHAPE /…
      // there sets glassTypeCode from the Glass Type Master. Do NOT overwrite that
      // with the item's material glass type (e.g. "annealed"); only fall back to
      // the item spec when the row has no TYPE-derived code yet.
      glassTypeCode: cur.glassTypeCode || it.glassTypeCode || null,
      // Item's glass colour family (clear/tinted/reflective/…) drives family-scoped
      // Other Charges (e.g. Color Shape). Null for non-glass items.
      glassFamily: it.glassFamily || null,
      // Stage 6OC.3 — item's glass polish (from the Item Master) drives
      // polish-bound Other Charges. Null for non-glass / unpolished items.
      polishTypeCode: it.polishTypeCode || null,
      // Default selling UOM fetched from the Item Master (e.g. PCS / NOS / SET for
      // hardware). Kept on the snapshot — the proforma price UOM enum stays SQF/SQM.
      itemSnapshot: {
        uomCode: it.defaultUomCode || null,
        rateUomCode: it.defaultUomCode || null,
        minSalesRate: it.minSalesRate != null ? Number(it.minSalesRate) : null,
        maxSalesRate: it.maxSalesRate != null ? Number(it.maxSalesRate) : null
      },
      // Stock identity for nesting: a finished item is cut from its base stock
      // (source_stock_item_id); a raw stock item is its own stock.
      stockItemId: it.sourceStockItemId || it.id,
      stockItemName: it.sourceStockItemName || it.itemName || it.itemDesc || null,
      // Stage 6PL-1c — the picker already asked the server for THIS customer's
      // rate (proformaApi.itemLookup), so the row records which list answered.
      // Null when no list covers the item: the master priced it, and the server
      // stamps MASTER on save.
      priceListId: it.priceListId || null,
      priceSource: it.priceSource || null
    }));
    // No default selling price on the master → nothing to auto-fill. Tell the user
    // so they know to set the item's Sales Rate in the Item Master (or type a price
    // here). A SHEET stock item is priced by area × rate / scrap, not a per-unit
    // sales rate, so it is exempt from this notice.
    const isSheetStock = typeUp(it.itemType) === 'SHEET'
      || (it.isStockItem && !it.sourceStockItemId && /\bSHEET\b/i.test(String(it.itemName || it.itemDesc || '')));
    // Stage 6PL-1c — the price list quoted this rate in a UOM that has no
    // conversion to the item's (e.g. a PCS rate on an area-sold item), so it
    // was used AS TYPED rather than silently converted. Say so: a wrong
    // conversion is worse than an honest warning, but a silent one is worst.
    if (it.priceUomMismatch) {
      message.warning({
        content: `The price list quotes ${it.itemName || it.itemDesc || 'this item'} in a different unit from the item's own (${it.defaultUomCode || '—'}). The rate was applied as-is — check it before saving.`,
        duration: 6
      });
    }
    if (!(num(salesRate) > 0) && !(num(it.salesRate) > 0) && !isSheetStock) {
      const name = it.itemName || it.itemDesc || 'This item';
      message.warning({
        content: `${name} has no default selling price in the Item Master. Enter a price in the grid, or set the item's Sales Rate in the Item Master to auto-fill next time.`,
        duration: 5
      });
    }
  }, [updateRow, header.priceUomCode]);

  // Pick a COMBO / set item → expand it into a parent line + one child per
  // component (glass children = area lines, hardware = per-piece). The picked
  // blank row becomes the combo parent; components are inserted as its children.
  const pickCombo = useCallback(async (refUuid, it) => {
    if (!it || !it.id) return;
    let data;
    try { data = await proformaApi.expandCombo(it.id); }
    catch (e) {
      message.error((e && e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'Could not load combo components.');
      return;
    }
    if (!data || !data.combo || !Array.isArray(data.components) || data.components.length === 0) {
      message.warning('This item has no combo components.');
      return;
    }
    setLines((rows) => {
      const idx = rows.findIndex((r) => r.refUuid === refUuid);
      if (idx < 0) return rows;
      const base = rows[idx];
      const parentRef = base.refUuid;
      const lvl = num(base.level);
      const parent = {
        ...base,
        type: 'COMBO',
        itemId: data.combo.itemId,
        itemDesc: data.combo.itemDesc, rawItemDesc: data.combo.itemDesc,
        gstRate: num(data.combo.gstRate) || num(base.gstRate) || 18,
        hsnSac: data.combo.hsnSac || null, taxability: data.combo.taxability || null,
        materialTypeCode: data.combo.materialTypeCode || null, categoryCode: data.combo.categoryCode || null,
        glassTypeCode: null, thicknessMm: null,
        qty: num(base.qty) > 0 ? num(base.qty) : 1,
        salesRate: 0, alternateRate: 0,
        actualWidthMm: 0, actualHeightMm: 0, manualChargeWidthMm: 0, manualChargeHeightMm: 0,
        isExpanded: true,
        nestingConfig: {
          ...(base.nestingConfig || {}),
          combo: {
            sourceItemId: data.combo.itemId, setType: data.combo.setType,
            priceMode: data.combo.priceMode === 'FIXED' ? 'FIXED' : 'SUM',
            comboRate: num(data.combo.comboRate)
          }
        }
      };
      const children = data.components.map((c) => {
        const ch = blankLine(1, lvl + 1, parentRef);
        ch.type = c.lineType || '';
        ch.itemId = c.itemId || null;
        ch.itemDesc = c.itemDesc || ''; ch.rawItemDesc = c.itemDesc || '';
        ch.qty = num(c.qty) > 0 ? num(c.qty) : 1;
        ch.salesRate = num(c.salesRate);
        ch.gstRate = num(c.gstRate) || num(parent.gstRate) || 18;
        ch.hsnSac = c.hsnSac || null; ch.taxability = c.taxability || null;
        ch.materialTypeCode = c.materialTypeCode || null; ch.categoryCode = c.categoryCode || null;
        ch.glassTypeCode = c.glassTypeCode || null;
        ch.thicknessMm = c.thicknessMm != null ? num(c.thicknessMm) : null;
        ch.stockItemId = c.stockItemId || null; ch.stockItemName = c.stockItemName || null;
        ch.location = base.location || '';
        ch.priceUomCode = base.priceUomCode || null;
        ch.itemSnapshot = { uomCode: c.uomCode || null };
        ch.nestingConfig = { comboComponent: { optional: c.optional === true, seq: c.seq } };
        return ch;
      });
      const next = rows.slice();
      next.splice(idx, 1, parent, ...children);
      return rebuildTree(groupSheetChildren(next));
    });
  }, [setLines]);

  // Toggle a combo parent between SUM (bill each component) and FIXED (bill one
  // combo price). Only the combo parent's nestingConfig.combo.priceMode changes.
  const setComboPriceMode = useCallback((refUuid, mode) => {
    setLines((rows) => rows.map((r) => {
      if (r.refUuid !== refUuid) return r;
      const combo = (r.nestingConfig && r.nestingConfig.combo) || {};
      return { ...r, nestingConfig: { ...(r.nestingConfig || {}), combo: { ...combo, priceMode: mode } } };
    }));
  }, [setLines]);

  const gstOptions = useMemo(() => {
    const list = (dropdowns.gstRates || []).map((g) => {
      const value = g.value != null ? Number(g.value) : Number(g.rate);
      return { label: g.label || `${value}%`, value };
    });
    if (list.length === 0) [0, 5, 12, 18, 28].forEach((v) => list.push({ label: `${v}%`, value: v }));
    return list;
  }, [dropdowns.gstRates]);

  const fabCols = useMemo(() => {
    const active = selectGridFabrications(dropdowns.fabrications);
    const limit = header.otherFab ? active.length : 5;
    const cols = buildFabricationColumns(active, limit);
    // Per-document fabrication price overrides from the bottom panel apply to
    // NEW counts entered in the grid (existing entries are updated in place).
    const overrides = header.fabRates || {};
    return cols.map((c) => (overrides[c.code] != null ? { ...c, rate: num(overrides[c.code]) } : c));
  }, [dropdowns.fabrications, header.otherFab, header.fabRates]);
  const baseColumns = useMemo(() => buildProformaColumns({
    fabricationColumns: fabCols,
    inputUnit: header.inputUnitCode || 'MM',
    areaVisible: header.areaVisible,
    showLocation: header.showLocation === true || header.groupByLoc === true,
    editChargeable: header.editChargeable === true,
    // Stage 6CM.2 — Company Defaults chargeable mode drives CHRG W/H cell lock.
    chargeableMode: header.chargeableMode,
    // Drives the PRICE column headers (primary = document Price UOM).
    priceUomCode: header.priceUomCode,
    // Tenant-wide hidden columns (Proforma Grid Columns settings page).
    hiddenColumns: header.hiddenGridColumns
  }), [fabCols, header.inputUnitCode, header.areaVisible, header.showLocation, header.groupByLoc, header.editChargeable, header.chargeableMode, header.priceUomCode, header.hiddenGridColumns]);
  // ITEM DESC is user-resizable (drag the header's right edge). The chosen width
  // overrides the column model; everything downstream (colLeft / totalWidth /
  // sticky offsets) is derived from column widths, so the override flows through.
  const columns = useMemo(() => (
    itemDescWidth > 0
      ? baseColumns.map((c) => (c.key === 'itemDesc' ? { ...c, width: itemDescWidth } : c))
      : baseColumns
  ), [baseColumns, itemDescWidth]);
  const { colLeft, colRight, totalWidth, lastFixedLeftKey } = useMemo(() => computeColumnLayout(columns), [columns]);
  const navKeys = useMemo(() => columns.filter((c) => NAV_KINDS.includes(c.kind)).map((c) => c.key), [columns]);

  // Which data field(s) a nav column copies for the fill-down shortcut. Reuses
  // the drag-copy map where defined (ITEM DESC → description only, type/location/
  // rates), otherwise falls back to the column's own field (width/height/qty/…).
  // Returns null for columns with no simple single field to copy (e.g. fabrication).
  // Defined after `columns` so it can read them without a temporal-dead-zone.
  const fillFieldsFor = useCallback((colKey) => {
    if (FILL_FIELDS[colKey]) return FILL_FIELDS[colKey];
    const col = columns.find((c) => c.key === colKey);
    if (!col) return null;
    if (col.kind === 'item') return ITEM_DESC_FILL_FIELDS;   // description only, not the whole row
    if (col.kind === 'dim' && col.mmField) return [col.mmField];
    if (col.kind === 'fab') return null;   // per-cell fab count — not a plain field
    if (col.field) return [col.field];
    return null;
  }, [columns]);
  const colMeta = useMemo(() => {
    const m = { __frozenLeft: 0, __frozenRight: 0 };
    for (const c of columns) {
      m[c.key] = { left: colLeft[c.key], width: c.width, fixed: c.fixed || null };
      if (c.fixed === 'left') m.__frozenLeft += c.width;
      else if (c.fixed === 'right') m.__frozenRight += c.width;
    }
    return m;
  }, [columns, colLeft]);

  // rebuildTree → rows carry rowNo + hasChildren; computeTreeRows → views (money
  // + parent roll-ups + SHEET scrap), aligned by index to `treeRows`.
  // Perf: the parent already prices the document once (computeProformaPreview →
  // these same computeTreeRows rows) and passes them as `pricedRows`. Reuse them
  // so a keystroke prices the document ONCE, not twice (parent + grid). Fall back
  // to local pricing when the prop is absent (standalone/demo callers).
  const treeRows = useMemo(() => rebuildTree(lines), [lines]);
  const views = useMemo(
    () => (pricedRows != null ? pricedRows : computeTreeRows(header, lines).rows),
    [pricedRows, header, lines]
  );
  // Join the LIVE tree (`treeRows`, from live `lines`) to the priced `views` by
  // refUuid, NOT by array index. `views` can lag the tree by one render (the
  // deferred preview in ProformaEditorPage), so during the frame after an
  // insert/delete an index-join could momentarily pair a row with a NEIGHBOUR's
  // computed money. Keying on refUuid makes that impossible: a row shows its OWN
  // view (at worst one frame stale) or EMPTY_VIEW (blank) — never another row's.
  // Stabilise view identity across recomputes (see shallowEqualView) so the
  // memoized GridRow re-renders ONLY for rows whose numbers actually changed —
  // not the whole visible grid on every keystroke. Reuses the prior render's view
  // object for any row whose computed fields are unchanged.
  const prevViewByRef = useRef(new Map());
  const viewByRef = useMemo(() => {
    const prev = prevViewByRef.current;
    const m = new Map();
    if (Array.isArray(views)) {
      for (const v of views) {
        if (!v || !v.refUuid) continue;
        const old = prev.get(v.refUuid);
        m.set(v.refUuid, old && shallowEqualView(old, v) ? old : v);
      }
    }
    prevViewByRef.current = m;
    return m;
  }, [views]);

  // ── Nesting overflow alert ──
  // A SHEET group's cut parts must fit the mother sheet: total cut area cannot
  // exceed the available stock area, and no single part may be larger than every
  // sheet of its material. Both conditions are already computed per material
  // (overCut / oversized) and shown as chips in the NESTING bar; here we ALSO
  // raise an active toast when a group first crosses into overflow, so the user
  // is stopped from silently over-allocating. We remember the prior per-group
  // overflow state in a ref and alert only on the transition INTO overflow (not
  // on every keystroke while already over), keyed by master refUuid + material.
  const overflowSeenRef = useRef(new Set());
  useEffect(() => {
    if (ro) return;
    const rows = Array.isArray(views) ? views : [];
    const nowOver = new Set();
    const fresh = [];
    for (const mv of rows) {
      if (!mv || typeUp(mv.type) !== 'SHEET' || mv.parentRefUuid || !mv.hasChildren) continue;
      const sqmP = ['SQM', 'SQMT'].includes(String(header.priceUomCode || '').toUpperCase());
      const aLbl = sqmP ? 'SQM' : 'SQFT';
      const bd = Array.isArray(mv.materialBreakdown) && mv.materialBreakdown.length
        ? mv.materialBreakdown : [{ material: '', overCut: num(mv.cutSqft) > num(mv.sheetSqft), oversized: num(mv.oversizedCuts), sheetSqm: mv.sheetSqm, cutSqm: mv.cutSqm, sheetSqft: mv.sheetSqft, cutSqft: mv.cutSqft }];
      for (const b of bd) {
        const over = !!b.overCut || num(b.oversized) > 0;
        if (!over) continue;
        const key = `${mv.refUuid}::${b.material || '-'}`;
        nowOver.add(key);
        if (!overflowSeenRef.current.has(key)) {
          const sheetA = num(sqmP ? b.sheetSqm : b.sheetSqft);
          const cutA = num(sqmP ? b.cutSqm : b.cutSqft);
          const matTxt = b.material ? ` (${b.material})` : '';
          if (num(b.oversized) > 0) {
            fresh.push(`Part too large for the sheet${matTxt}: ${num(b.oversized)} part${num(b.oversized) === 1 ? '' : 's'} exceed every available sheet size. Reduce the part size or add a larger sheet.`);
          } else {
            fresh.push(`Cut area exceeds the sheet${matTxt}: parts total ${cutA.toFixed(3)} ${aLbl} but only ${sheetA.toFixed(3)} ${aLbl} of stock is available. Reduce parts or add more sheets.`);
          }
        }
      }
    }
    // Emit one toast per newly-overflowing group (deduped), keep the bar chips for
    // the persistent state.
    for (const msg of fresh) message.warning({ content: msg, duration: 5 });
    overflowSeenRef.current = nowOver;
  }, [views, ro, header.priceUomCode]);

  // Rows currently covered by an active drag-fill (src + the fillable rows down
  // to the hovered row) — used to paint the highlight.
  const fillHiSet = useMemo(() => {
    if (!fillDrag) return null;
    const sIdx = treeRows.findIndex((r) => r.refUuid === fillDrag.srcRef);
    if (sIdx < 0) return null;
    const set = new Set([fillDrag.srcRef]);
    // Assembly drag: highlight the exact unit slots that will be replaced (each
    // target's whole subtree), so the range preview matches what gets cloned.
    if (fillDrag.mode === 'assembly') {
      const plan = planAssemblyDragCopy(treeRows, fillDrag.srcRef, fillDrag.overRef);
      if (plan) plan.targets.forEach((t) => { for (let i = t.idx; i < t.end; i += 1) set.add(treeRows[i].refUuid); });
      return set;
    }
    const oIdx = treeRows.findIndex((r) => r.refUuid === fillDrag.overRef);
    const hi = oIdx > sIdx ? oIdx : sIdx;
    for (let i = sIdx + 1; i <= hi; i += 1) {
      const r = treeRows[i];
      if (!r.hasChildren && !r.parentRefUuid) set.add(r.refUuid);
    }
    return set;
  }, [fillDrag, treeRows]);

  // grouped header bands
  const groups = useMemo(() => {
    const out = []; let i = 0;
    while (i < columns.length) {
      const label = columns[i].group; let j = i;
      while (j < columns.length && columns[j].group === label) j += 1;
      const slice = columns.slice(i, j);
      out.push({
        label, left: colLeft[columns[i].key], width: slice.reduce((s, c) => s + c.width, 0),
        fixedLeft: slice.every((c) => c.fixed === 'left'), fixedRight: slice.every((c) => c.fixed === 'right')
      });
      i = j;
    }
    for (let k = out.length - 1; k >= 0; k -= 1) { if (out[k].fixedLeft) { out[k].lastFixedLeft = true; break; } }
    // Right offsets so multiple right-fixed groups (select + action) stack.
    let gr = 0;
    for (let k = out.length - 1; k >= 0; k -= 1) {
      if (out[k].fixedRight) { out[k].right = gr; gr += out[k].width; }
    }
    return out;
  }, [columns, colLeft]);

  // Visible display: hide descendants of collapsed rows; inject a NESTING bar
  // AFTER each expanded SHEET-master subtree (mirrors GlassMaster getVisible —
  // the bar sits at the BOTTOM of the group, and only when the group actually
  // has stock area, never on an empty master).
  const display = useMemo(() => {
    // pass 1 — honour collapse, collect the visible rows.
    const vis = []; const stack = [];
    for (let i = 0; i < treeRows.length; i += 1) {
      const r = treeRows[i]; const lvl = num(r.level);
      while (stack.length > lvl) stack.pop();
      if (stack.every(Boolean)) vis.push({ row: r, i, lvl });
      stack[lvl] = r.isExpanded !== false;
    }
    // pass 2 — emit rows, dropping a NESTING bar after each sheet group.
    const out = [];
    for (let vi = 0; vi < vis.length; vi += 1) {
      const it = vis[vi]; const r = it.row; const mv = viewByRef.get(r.refUuid) || EMPTY_VIEW;
      out.push({ kind: 'row', row: r, i: it.i });
      const isSheetMaster = typeUp(r.type) === 'SHEET' && !r.parentRefUuid && r.hasChildren && r.isExpanded !== false;
      if (isSheetMaster && num(mv.sheetSqft) > 0) {
        const masterLvl = num(r.level);
        let end = vi;
        for (let nx = vi + 1; nx < vis.length; nx += 1) {
          if (num(vis[nx].lvl) > masterLvl) end = nx; else break;
        }
        for (let ci = vi + 1; ci <= end; ci += 1) out.push({ kind: 'row', row: vis[ci].row, i: vis[ci].i });
        vi = end;
        // One NESTING bar per material in the group (per-material stock/scrap).
        const breakdown = Array.isArray(mv.materialBreakdown) && mv.materialBreakdown.length
          ? mv.materialBreakdown : [null];
        breakdown.forEach((mat, mi) => out.push({ kind: 'nest', master: mv, mat, matIdx: mi, row: r, i: it.i }));
      }
    }
    return out;
  }, [treeRows, viewByRef]);

  // ── virtualization ──
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(640);
  // PERF — coalesce scroll to one state update per animation frame. A fast wheel /
  // trackpad can fire several scroll events within a single frame; setting state on
  // each one re-renders the virtual window (and mounts/unmounts AntD cells) more
  // than once per paint for no visual gain. rAF-batching caps that at one re-render
  // per frame while always committing the LATEST scrollTop, so the window never lags.
  const scrollRafRef = useRef(0);
  const pendingScrollTopRef = useRef(0);
  const onScroll = useCallback((e) => {
    pendingScrollTopRef.current = e.currentTarget.scrollTop;
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      setScrollTop(pendingScrollTopRef.current);
    });
  }, []);
  useEffect(() => () => { if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current); }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setViewportH(el.clientHeight || 640);
    measure();
    const robs = new ResizeObserver(measure);
    robs.observe(el);
    return () => robs.disconnect();
  }, []);
  const total = display.length;
  const startIdx = Math.max(0, Math.floor((scrollTop - HEADER_H) / ROW_H) - VROW_BUFFER);
  const endIdx = Math.min(total, Math.ceil((scrollTop + viewportH - HEADER_H) / ROW_H) + VROW_BUFFER);
  const visible = display.slice(startIdx, endIdx);
  const topPad = startIdx * ROW_H;
  const botPad = Math.max(0, (total - endIdx) * ROW_H);

  // ── keyboard: cell nav + Ctrl/Ctrl-Shift nesting shortcuts ──
  const refAtVis = useCallback((vis_, vIdx) => {
    const it = vis_[vIdx];
    return it && it.kind === 'row' ? it.row.refUuid : null;
  }, []);
  const onKeyDown = useCallback((e) => {
    const target = e.target;
    const parsed = target && parseCellId(target.id);
    if (!parsed) return;
    if (target.getAttribute && target.getAttribute('aria-expanded') === 'true'
        && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) return;
    const [vIdx, cIdx] = parsed;
    const curRef = refAtVis(display, vIdx);

    if (e.ctrlKey || e.metaKey) {
      // A HELD key must never machine-gun row inserts: the OS repeat fires
      // keydown ~30×/s, each triggering a full-tree rebuild, which races the
      // state updates and balloons the grid toward MAX_LINES until the page
      // freezes. Every insert / nesting shortcut is a discrete, intentional
      // press — ignore auto-repeat. (Plain arrow navigation below still repeats.)
      if (e.repeat) { e.preventDefault(); return; }
      const k = e.key.toLowerCase();
      if (!curRef) return;
      // Ctrl+'  (ditto) — fill down: copy this column's value from the row ABOVE
      // into the current cell (on ITEM DESC it copies the whole row, matching the
      // drag). Mirrors Excel/Sheets "copy from the cell above"; Ctrl+D is left to
      // the browser's bookmark shortcut.
      if (k === "'" && !e.shiftKey) {
        e.preventDefault(); e.stopPropagation();
        const fields = fillFieldsFor(navKeys[cIdx]);
        if (!fields) return;
        let aboveIdx = vIdx - 1;
        while (aboveIdx >= 0 && display[aboveIdx] && display[aboveIdx].kind !== 'row') aboveIdx -= 1;
        const srcRef = aboveIdx >= 0 ? refAtVis(display, aboveIdx) : null;
        if (srcRef) fillCellFromRow(srcRef, curRef, fields);
        return;
      }
      if (!e.shiftKey && e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); insertRow(curRef, 'above'); return; }
      if (!e.shiftKey && e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); insertRow(curRef, 'below'); return; }
      // Ctrl+Delete — easy two-key row delete (a comfier alias for Ctrl+Shift+X);
      // keep the cursor put: focus lands on the row that shifts up into this slot.
      if (!e.shiftKey && e.key === 'Delete') { e.preventDefault(); e.stopPropagation(); removeRow(curRef); focusVisCell(vIdx, cIdx); return; }
      if (e.shiftKey) {
        const row = treeRows.find((r) => r.refUuid === curRef);
        const isSheetMaster = row && typeUp(row.type) === 'SHEET' && !row.parentRefUuid;
        if (k === 'n') { e.preventDefault(); e.stopPropagation(); insertChild(curRef, null); return; }
        if (k === 'x') { e.preventDefault(); e.stopPropagation(); removeRow(curRef); focusVisCell(vIdx, cIdx); return; }
        if (k === 'l') { e.preventDefault(); e.stopPropagation(); setLamiTarget(curRef); return; }
        if (k === 'u') { e.preventDefault(); e.stopPropagation(); setDguTarget({ refUuid: curRef, lamiDgu: false }); return; }
        if (k === 'm') { e.preventDefault(); e.stopPropagation(); setDguTarget({ refUuid: curRef, lamiDgu: true }); return; }
        if (k === 'o') { e.preventDefault(); e.stopPropagation(); setGlassCfgTarget(curRef); return; }
        if (k === 'e' && isSheetMaster) { e.preventDefault(); e.stopPropagation(); insertChild(curRef, LINE_TYPE.SHEET); return; }
        if (k === 'r' && isSheetMaster) { e.preventDefault(); e.stopPropagation(); insertChild(curRef, ''); return; }
      }
      return;
    }
    // ── Wastage brush (ACTUAL column) ──────────────────────────────────
    // Shift + ↓ / ↑ while the cursor is in an ACTUAL W or ACTUAL H cell stamps the
    // CURRENT Wastage-Mode tolerance onto THIS row's chargeable size for that one
    // column (ACT W → charge width, ACT H → charge height), then navigates as
    // normal. This lets the user "paint" wastage onto exactly the rows they arrow
    // over, driven from the always-navigable ACTUAL column (the CHRG column is
    // usually locked). Same result as typing the actual size — chargeStampForActual
    // → the per-row manualCharge override (0 when there's no tolerance → the row
    // falls back to ACTUAL). Changing the Wastage Mode never touches a row, so this
    // brush is how the new setting is re-applied to the rows the user chooses.
    if (e.shiftKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && curRef) {
      const colKey = navKeys[cIdx];
      if (colKey === 'actW' || colKey === 'actH') {
        const row = treeRows.find((r) => r.refUuid === curRef);
        // Brush a normal LEAF row, OR a LAMI/DGU/LAMI+DGU assembly PARENT. The
        // assembly bills as Σ(plies), so updateRow flows the parent's stamped
        // chargeable down to every ply (see the LAMI/DGU block there). SHEET masters
        // (hasChildren, not an assembly) and piece rows stay excluded — their charge
        // means something different (stock/scrap area) and isn't wastage-brushed.
        const brushable = row && !isPieceType(row.type)
          && (!row.hasChildren || isLamiOrDgu(row.type));
        if (brushable) {
          if (colKey === 'actW') {
            updateRow(curRef, { manualChargeWidthMm: chargeStampForActual(num(row.actualWidthMm), header.wastageMode, header.wastageWidthMm) });
          } else {
            updateRow(curRef, { manualChargeHeightMm: chargeStampForActual(num(row.actualHeightMm), header.wastageMode, header.wastageHeightMm) });
          }
        }
      }
    }
    const caretAtStart = () => { try { const s = target.selectionStart; const en = target.selectionEnd; if (s == null || en == null) return true; return s === 0 && en === 0; } catch (_e) { return true; } };
    const caretAtEnd = () => { try { const s = target.selectionStart; const en = target.selectionEnd; if (s == null || en == null) return true; const len = (target.value || '').length; return s === len && en === len; } catch (_e) { return true; } };
    // NUMERIC cells (mm width/height/qty/rate/fab) get IMMEDIATE Left/Right column
    // jumps — the value is auto-selected on focus, so a caret-gated arrow would
    // waste the first press just collapsing the selection (the "odd" feeling).
    // Free-text cells (ITEM DESC, glass-type, inch fractions) stay caret-aware so
    // the cursor can still move inside the text, and only jump columns at the edge.
    const isNumericCell = !!(target.classList && target.classList.contains('ant-input-number-input'));
    // ── Tab on an OPEN item-search dropdown ────────────────────────────────
    // Commit the ↓-highlighted MASTER item (never the raw typed text), then
    // advance one column. If the popup is open but nothing is highlighted, Tab is
    // blocked so no free text slips past. (↑/↓/Enter are already handed to AntD by
    // the aria-expanded guard above; only Tab reaches here while the popup is open.)
    //
    // EXCEPTION — the "#process" picker: when the ITEM DESC value carries a '#'
    // tag, the popup is the process picker, NOT the item search. The typed tag is
    // already committed on every keystroke (commitRaw parses base + tags), so Tab
    // must be free to walk out of the cell — never blocked. A highlighted process
    // option is still committed on Tab; with none highlighted, Tab just navigates.
    if (e.key === 'Tab' && !e.shiftKey
        && target.getAttribute && target.getAttribute('aria-expanded') === 'true') {
      const isProcessPicker = String(target.value || '').includes('#');
      const res = tabSelectActiveOption(target);
      if (res === 'blocked' && !isProcessPicker) { e.preventDefault(); e.stopPropagation(); return; }
      if (res === 'selected') {
        e.preventDefault(); e.stopPropagation();
        // Advance to the next column AFTER AntD closes the popup + the fill applies.
        // Blur the combobox first so its focus management doesn't pull focus back.
        const ac = target;
        const nextC = Math.max(0, Math.min(cIdx + 1, navKeys.length - 1));
        const go = () => {
          try { if (ac && ac.blur) ac.blur(); } catch (_e) { /* noop */ }
          const nextEl = document.getElementById(`pfc-${vIdx}-${nextC}`);
          if (nextEl) { try { nextEl.focus({ preventScroll: true }); } catch (_e) { nextEl.focus(); } if (nextEl.select) nextEl.select(); }
        };
        requestAnimationFrame(go);
        setTimeout(go, 60);
        return;
      }
      // res === null (popup empty) → fall through to the normal Tab move, which
      // keeps any free-typed SERVICE/OTHER text intact.
    }
    let dR = 0; let dC = 0;
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); e.stopPropagation(); dR = 1; break;
      case 'ArrowUp': e.preventDefault(); e.stopPropagation(); dR = -1; break;
      case 'Enter': e.preventDefault(); e.stopPropagation(); dR = 1; break;
      case 'Tab': e.preventDefault(); e.stopPropagation(); dC = e.shiftKey ? -1 : 1; break;
      case 'ArrowLeft': if (!isNumericCell && !caretAtStart()) return; e.preventDefault(); e.stopPropagation(); dC = -1; break;
      case 'ArrowRight': if (!isNumericCell && !caretAtEnd()) return; e.preventDefault(); e.stopPropagation(); dC = 1; break;
      default: return;
    }
    // Move within the visible list, skipping nesting bars.
    let newR = vIdx + dR;
    while (newR >= 0 && newR < display.length && display[newR] && display[newR].kind !== 'row') newR += (dR >= 0 ? 1 : -1);
    newR = Math.max(0, Math.min(newR, display.length - 1));
    const newC = Math.max(0, Math.min(cIdx + dC, navKeys.length - 1));
    const el = document.getElementById(`pfc-${newR}-${newC}`);
    if (el) {
      try { el.focus({ preventScroll: true }); } catch (_e) { el.focus(); }
      const sc = scrollRef.current;
      const meta = colMeta[navKeys[newC]];
      if (sc && meta && meta.fixed == null) {
        const GAP = 8;
        const frozenL = colMeta.__frozenLeft; const frozenR = colMeta.__frozenRight;
        const viewLeft = sc.scrollLeft + frozenL;
        const viewRight = sc.scrollLeft + sc.clientWidth - frozenR;
        if (meta.left < viewLeft) sc.scrollLeft = Math.max(0, meta.left - frozenL - GAP);
        else if (meta.left + meta.width > viewRight) sc.scrollLeft = meta.left + meta.width - sc.clientWidth + frozenR + GAP;
      }
      // Keep the focused ROW in view vertically. We focus with preventScroll
      // (so the browser doesn't fight our horizontal logic or the sticky
      // header), which means the container never scrolls to follow arrow/Enter
      // navigation on its own — do it here. The row at display index `newR`
      // sits at content-space top HEADER_H + newR*ROW_H; the sticky header
      // covers the top HEADER_H of the viewport, so the first usable pixel is
      // scrollTop + HEADER_H. Scroll the minimum needed to reveal the row just
      // under the header (moving up) or just above the bottom edge (moving down).
      if (sc) {
        const VGAP = 4;
        const rowTop = HEADER_H + newR * ROW_H;
        const rowBottom = rowTop + ROW_H;
        const viewTop = sc.scrollTop + HEADER_H;
        const viewBottom = sc.scrollTop + sc.clientHeight;
        if (rowTop < viewTop) sc.scrollTop = Math.max(0, rowTop - HEADER_H - VGAP);
        else if (rowBottom > viewBottom) sc.scrollTop = rowBottom - sc.clientHeight + VGAP;
      }
      if (el.select) el.select();
    }
  }, [display, navKeys, colMeta, treeRows, insertRow, insertChild, removeRow, refAtVis,
    updateRow, fillFieldsFor, fillCellFromRow, focusVisCell, header.wastageMode, header.wastageWidthMm, header.wastageHeightMm]);

  // context-menu items for the right-clicked row
  const ctxRow = contextMenu ? treeRows.find((r) => r.refUuid === contextMenu.refUuid) : null;
  const ctxType = ctxRow ? typeUp(ctxRow.type) : '';
  const ctxIsSheetMaster = ctxRow && ctxType === 'SHEET' && !ctxRow.parentRefUuid;
  const ctxIsLami = ctxType === 'LAMI';
  const ctxIsLamiDgu = ctxType === 'LAMI+DGU';
  const ctxIsCombo = ctxIsLami || ctxType === 'DGU' || ctxIsLamiDgu;
  // Item-set combo (distinct from the LAMI/DGU "combination" above).
  const ctxIsItemCombo = ctxType === 'COMBO';
  const ctxComboMode = ctxIsItemCombo && ctxRow.nestingConfig && ctxRow.nestingConfig.combo
    ? String(ctxRow.nestingConfig.combo.priceMode || 'SUM').toUpperCase() : 'SUM';
  // Edge picker applies only when the row carries an RFT/RMT (running-feet) process.
  const ctxHasRft = ctxRow && hasRftProcess(ctxRow.processPricing);
  // "Copy unit down to…" is offered only for a LAMI/DGU cut-piece that lives in a
  // SHEET group AND has at least one cut-piece row below it to replace.
  const ctxUnitCopy = contextMenu ? planUnitCopy(lines, contextMenu.refUuid) : null;
  const ctxCanCopyUnit = !!(ctxUnitCopy && ctxUnitCopy.targets.length > 0);
  const closeCtx = () => setContextMenu(null);

  return (
    <div className="pfg-wrap">
      <style>{GRID_CSS}</style>
      <div ref={scrollRef} className="pfg-scroll" onKeyDownCapture={onKeyDown} onScroll={onScroll}>
        <div className="pfg-grid" style={{ width: totalWidth, minWidth: '100%' }}>
          <div className="pfg-header">
            <div className="pfg-hgroups" style={{ width: totalWidth }}>
              {groups.map((g) => {
                const fixCls = (g.fixedLeft || g.fixedRight) ? ' pfg-hfix' : '';
                const lastCls = g.fixedLeft && g.lastFixedLeft ? ' pfg-hfix-last' : '';
                return (
                  <div key={`g-${g.label}-${g.left}`} className={`pfg-ghdr${fixCls}${lastCls}`}
                    style={{ width: g.width, height: GROUP_H, ...(g.fixedLeft ? { position: 'sticky', left: g.left } : {}), ...(g.fixedRight ? { position: 'sticky', right: g.right || 0 } : {}) }}>
                    {g.label}
                  </div>
                );
              })}
            </div>
            <div className="pfg-hleaves" style={{ width: totalWidth }}>
              {columns.map((col) => {
                const isFL = col.fixed === 'left'; const isFR = col.fixed === 'right';
                const fixCls = (isFL || isFR) ? ' pfg-hfix' : '';
                const lastCls = isFL && col.key === lastFixedLeftKey ? ' pfg-hfix-last' : '';
                return (
                  <div key={col.key} className={`pfg-lhdr${col.bg ? ' ' + col.bg : ''}${fixCls}${lastCls}`}
                    style={{ width: col.width, height: LEAF_H, ...(isFL ? { position: 'sticky', left: colLeft[col.key] } : {}), ...(isFR ? { position: 'sticky', right: (colRight && colRight[col.key]) || 0 } : {}) }}>
                    {col.key === 'select'
                      ? <Checkbox checked={allChecked} indeterminate={someChecked} disabled={ro}
                          onChange={(e) => toggleSelectAll(e.target.checked)} title="Select all" />
                      : col.header}
                    {col.key === 'itemDesc' ? (
                      <span className="pfg-colresize" title="Drag to resize ITEM DESC · double-click to reset"
                        onMouseDown={startColResize} onDoubleClick={resetColResize} />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {topPad > 0 ? <div aria-hidden style={{ height: topPad }} /> : null}
          {visible.map((d, k) => {
            const vi = startIdx + k;
            if (d.kind === 'nest') {
              const m = d.master;
              // Per-material row (one bar per material). Fall back to the master
              // aggregate when there is no breakdown (e.g. unnamed single sheet).
              const b = d.mat || {
                material: '', sheetSqft: m.sheetSqft, cutSqft: m.cutSqft,
                scrapSqft: m.scrapSqft, sheetSqm: m.sheetSqm, cutSqm: m.cutSqm,
                scrapSqm: m.scrapSqm, utilization: m.utilization,
                scrapRate: d.row.scrapRate, scrapValue: m.scrapValue
              };
              // Show the nesting areas in the document's Price UOM (SQM or SQFT).
              const sqmP = ['SQM', 'SQMT'].includes(String(header.priceUomCode || '').toUpperCase());
              const aLbl = sqmP ? 'SQM' : 'SQF';
              const sheetA = sqmP ? b.sheetSqm : b.sheetSqft;
              const cutA = sqmP ? b.cutSqm : b.cutSqft;
              const scrapA = sqmP ? b.scrapSqm : b.scrapSqft;
              const pct = num(b.utilization);
              const pctColor = pct >= 85 ? '#059669' : pct >= 60 ? '#d97706' : '#dc2626';
              const firstRow = num(d.matIdx) === 0;
              const cfg = d.row.nestingConfig || {};
              const sheetMode = resolveSheetCostMode(cfg);
              const isWastageMode = sheetMode !== 'SHEET_PRICE';
              const setSheetMode = (mode) => {
                const next = { ...cfg, sheetCostMode: mode };
                updateRow(d.row.refUuid, { nestingConfig: next });
              };
              // AntD InputNumber hands us the PARSED value (number | null) directly
              // and buffers intermediate text ("2.", "0.") internally, so decimals /
              // leading zeros type cleanly — unlike a raw <input type=number> that
              // re-parsed + reflected on every keystroke (the "value changes by itself"
              // bug: each keystroke recomputed the grid and pushed the number back).
              const onRate = (val) => {
                const rate = Number.isFinite(val) && val > 0 ? val : null;
                const prevRates = (cfg.scrapRates && typeof cfg.scrapRates === 'object') ? cfg.scrapRates : {};
                const nextRates = { ...prevRates };
                if (b.material) {
                  if (rate == null) delete nextRates[b.material]; else nextRates[b.material] = rate;
                }
                // Keep the legacy single-rate field in sync for the no-material case.
                const patch = { nestingConfig: { ...cfg, scrapRates: nextRates } };
                if (!b.material) patch.scrapRate = rate;
                updateRow(d.row.refUuid, patch);
              };
              return (
                <div key={`nest-${d.row.refUuid}-${d.matIdx}`} className="pfg-nestbar" style={{ width: totalWidth }}>
                  <div className="pfg-nestinner">
                    <span style={{ fontWeight: 700, letterSpacing: '.03em', minWidth: 64, visibility: firstRow ? 'visible' : 'hidden' }}>NESTING</span>
                    {/* Sheet cost mode — shown once per group (first material row).
                        WASTAGE = bill leftover scrap here; SHEET PRICE = price the
                        sheet line in the PRICE cell above. Mutually exclusive. */}
                    {firstRow ? (
                      <span className="pfg-scm" title="Choose how this sheet group is billed: its leftover scrap, OR a price on the sheet line.">
                        <button type="button" disabled={ro}
                          className={`pfg-scm__opt${isWastageMode ? ' is-on' : ''}`}
                          onClick={() => setSheetMode('WASTAGE')}>Wastage</button>
                        <button type="button" disabled={ro}
                          className={`pfg-scm__opt${!isWastageMode ? ' is-on' : ''}`}
                          onClick={() => setSheetMode('SHEET_PRICE')}>Sheet price</button>
                      </span>
                    ) : null}
                    {b.material
                      ? <span style={{ fontWeight: 700, color: '#6d28d9', minWidth: 130 }}>{b.material}</span>
                      : <span>▣ {num(m.sheetCount)} sheet{num(m.sheetCount) === 1 ? '' : 's'}</span>}
                    <span>Total: {num(sheetA).toFixed(3)} {aLbl}</span>
                    <span style={{ color: '#059669', fontWeight: 600 }}>Cut: {num(cutA).toFixed(3)}</span>
                    <span style={{ color: '#dc2626', fontWeight: 600 }}>Scrap: {num(scrapA).toFixed(3)}</span>
                    <span style={{ color: '#475569' }}>Utilized:</span>
                    <span style={{ fontWeight: 700, color: pctColor, background: '#fff', borderRadius: 4, padding: '1px 8px' }}>{pct}%</span>
                    {isWastageMode ? (
                      <>
                        <span style={{ marginLeft: 8, color: '#475569' }}>Scrap Rate:</span>
                        <InputNumber
                          size="small" min={0} controls={false} disabled={ro}
                          className="pfg-scraprate"
                          value={b.scrapRate != null ? b.scrapRate : null}
                          onChange={onRate}
                          placeholder="0"
                          style={{ width: 72 }}
                        />
                        {num(b.scrapValue) > 0
                          ? <span style={{ fontWeight: 700, color: '#92400e' }}>₹ {num(b.scrapValue).toFixed(2)}</span>
                          : <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>Set rate to bill scrap</span>}
                        {/* Per-group toggle: include this group's scrap in the base of
                            Jumbo/Shape/Template charges on its glass parts. Default ON.
                            Shown once per group (first material row). */}
                        {firstRow ? (
                          <label
                            title="When on, this sheet group's scrap value is added to the base of Jumbo / Shape / Template charges on its cut parts. Other charges are unaffected."
                            style={{ marginLeft: 10, display: 'inline-flex', alignItems: 'center', gap: 4, color: '#475569', fontSize: 11, cursor: ro ? 'default' : 'pointer' }}>
                            <input
                              type="checkbox" disabled={ro}
                              checked={cfg.includeScrapInPartCharge !== false}
                              onChange={(e) => updateRow(d.row.refUuid, { nestingConfig: { ...cfg, includeScrapInPartCharge: e.target.checked } })}
                            />
                            <span>Add scrap to shape/jumbo parts</span>
                          </label>
                        ) : null}
                      </>
                    ) : (
                      <span style={{ marginLeft: 8, fontStyle: 'italic', color: '#1d4ed8', fontWeight: 600 }}
                        title="Scrap costing is off — enter a rate in the sheet's PRICE cell above to bill the sheet line.">
                        Sheet priced above — scrap not billed
                      </span>
                    )}
                    {num(b.oversized) > 0
                      ? <span title="A part is larger than every sheet of this material (Rule 1)." style={{ fontWeight: 700, color: '#b91c1c', background: '#fee2e2', borderRadius: 4, padding: '1px 8px' }}>⚠ {num(b.oversized)} part{num(b.oversized) === 1 ? '' : 's'} exceed sheet</span>
                      : null}
                    {b.overCut
                      ? <span title="Cut area exceeds available stock — add more sheets (Rule 2: capped at 100%)." style={{ fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '1px 8px' }}>⚠ over-cut</span>
                      : null}
                  </div>
                </div>
              );
            }
            return (
              <GridRow key={`r-${d.row.refUuid}`} row={d.row} view={viewByRef.get(d.row.refUuid) || EMPTY_VIEW} vi={vi}
                columns={columns} colLeft={colLeft} colRight={colRight} lastFixedLeftKey={lastFixedLeftKey}
                totalWidth={totalWidth} ro={ro} linesLen={lines.length} priceUomCode={header.priceUomCode}
                dropdowns={dropdowns} gstOptions={gstOptions} navKeys={navKeys}
                jumboCondition={header.jumboCondition || null}
                updateRow={updateRow} removeRow={removeRow} pickItem={pickItem} pickCombo={pickCombo}
                startFillDrag={startFillDrag}
                fillHi={fillHiSet ? fillHiSet.has(d.row.refUuid) : false}
                fillHiCol={fillDrag ? fillDrag.colKey : null}
                selected={selectedRows.has(d.row.refUuid)} onToggleSelect={toggleSelect}
                onContextMenu={openContextMenu} onToggle={toggleExpand} onRftAdded={openEdgePicker} />
            );
          })}
          {botPad > 0 ? <div aria-hidden style={{ height: botPad }} /> : null}
        </div>
      </div>

      {/* Footer — append a NEW top-level line at the very end (outside any SHEET
          nesting group). This is the only in-UI path to add a line AFTER a sheet
          group when the group is the last section: the keyboard / context-menu
          "Add row below" keep a new sibling INSIDE the group (or add a sheet
          child on the master), so there was previously no way out. Edit mode only;
          disabled at the MAX_LINES cap. */}
      {!ro ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderTop: '1px solid #e2e8f0' }}>
          <Button size="small" type="dashed" icon={<PlusOutlined />}
            disabled={lines.length >= MAX_LINES}
            onClick={() => {
              addRow();
              // Reveal the appended row at the bottom so it isn't lost below the fold.
              requestAnimationFrame(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; });
            }}>
            Add line
          </Button>
          <span style={{ fontSize: 12, color: '#64748b' }}>Adds a line at the end, outside any sheet nesting group.</span>
        </div>
      ) : null}

      {contextMenu && !ro && ctxRow ? (
        <div className="pfg-ctx" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={(e) => e.stopPropagation()}>
          {/* ── Rows ── */}
          <span className="grp">Rows</span>
          <button onClick={() => { insertRow(contextMenu.refUuid, 'above'); closeCtx(); }}>
            <span className="ico" /><span className="lbl">Add row above</span><kbd>Ctrl ↑</kbd></button>
          <button onClick={() => { insertRow(contextMenu.refUuid, 'below'); closeCtx(); }}>
            <span className="ico" /><span className="lbl">Add row below</span><kbd>Ctrl ↓</kbd></button>
          <button onClick={() => { setAddRowsCount(5); setAddRowsTarget({ refUuid: contextMenu.refUuid }); closeCtx(); }}>
            <span className="ico" /><span className="lbl">Add multiple rows…</span></button>
          <button title="Duplicate this row with all its details below (the non-drag alternative to the fill-handle copy)"
            onClick={() => { setCopyRowsCount(1); setCopyRowsTarget({ refUuid: contextMenu.refUuid, hasChildren: ctxRow.hasChildren === true }); closeCtx(); }}>
            <span className="ico">⧉</span><span className="lbl">Copy multiple rows…</span></button>
          {ctxIsSheetMaster ? (
            <>
              <button onClick={() => { insertChild(contextMenu.refUuid, LINE_TYPE.SHEET); closeCtx(); }}>
                <span className="ico" /><span className="lbl">Add sheet size</span><kbd>Ctrl ⇧ E</kbd></button>
              <button onClick={() => { insertChild(contextMenu.refUuid, ''); closeCtx(); }}>
                <span className="ico" /><span className="lbl">Add part piece</span><kbd>Ctrl ⇧ R</kbd></button>
            </>
          ) : (
            <button onClick={() => { insertChild(contextMenu.refUuid, null); closeCtx(); }}>
              <span className="ico" /><span className="lbl">Add child row</span><kbd>Ctrl ⇧ N</kbd></button>
          )}

          {/* ── Structure (nesting) ── */}
          <div className="sep" />
          <span className="grp">Structure</span>
          {ctxIsCombo ? (
            <button className="set" onClick={() => {
              if (ctxIsLami) setLamiTarget(contextMenu.refUuid);
              else setDguTarget({ refUuid: contextMenu.refUuid, lamiDgu: ctxIsLamiDgu });
              closeCtx();
            }}><span className="ico">✎</span><span className="lbl">Edit {ctxType} combination</span></button>
          ) : null}
          {ctxCanCopyUnit ? (
            <button className="set" title="Replace the cut-piece rows below (in this sheet group) with an exact copy of this unit"
              onClick={() => {
                const max = ctxUnitCopy.targets.length;
                setCopyUnitCount(max);
                setCopyUnitTarget({ refUuid: contextMenu.refUuid, max });
                closeCtx();
              }}><span className="ico">⧉</span><span className="lbl">Copy {ctxType} unit down to…</span></button>
          ) : null}
          {ctxIsItemCombo ? (
            ctxComboMode === 'FIXED'
              ? <button className="set" onClick={() => { setComboPriceMode(contextMenu.refUuid, 'SUM'); closeCtx(); }}>
                  <span className="ico">₹</span><span className="lbl">Bill components individually</span></button>
              : <button className="set" onClick={() => { setComboPriceMode(contextMenu.refUuid, 'FIXED'); closeCtx(); }}>
                  <span className="ico">₹</span><span className="lbl">Bill as one combo price</span></button>
          ) : null}
          <button className="set" onClick={() => { setRowType(contextMenu.refUuid, LINE_TYPE.SHEET); closeCtx(); }}>
            <span className="ico" /><span className="lbl">Set as Sheet</span></button>
          <button className="set" onClick={() => { setLamiTarget(contextMenu.refUuid); closeCtx(); }}>
            <span className="ico" /><span className="lbl">Set as LAMI</span><kbd>Ctrl ⇧ L</kbd></button>
          <button className="set" onClick={() => { setDguTarget({ refUuid: contextMenu.refUuid, lamiDgu: false }); closeCtx(); }}>
            <span className="ico" /><span className="lbl">Set as DGU</span><kbd>Ctrl ⇧ U</kbd></button>
          <button className="set" onClick={() => { setDguTarget({ refUuid: contextMenu.refUuid, lamiDgu: true }); closeCtx(); }}>
            <span className="ico" /><span className="lbl">Set as LAMI+DGU</span><kbd>Ctrl ⇧ M</kbd></button>

          {/* ── Explain ── */}
          <div className="sep" />
          <span className="grp">Explain</span>
          <button onClick={() => { setRationaleTarget(contextMenu.refUuid); closeCtx(); }}>
            <span className="ico">₹</span><span className="lbl">Why this rate?</span></button>

          {/* ── Annotate ── */}
          <div className="sep" />
          <span className="grp">Annotate</span>
          <button onClick={() => { setNarrTarget(contextMenu.refUuid); closeCtx(); }}>
            <span className="ico">📝</span><span className="lbl">{ctxRow.narration ? 'Edit narration' : 'Add narration'}</span></button>
          {ctxHasRft ? (
            <button onClick={() => { setEdgeTarget(contextMenu.refUuid); closeCtx(); }}>
              <span className="ico">📐</span><span className="lbl">Edit edges (RFT/RMT)</span></button>
          ) : null}

          {/* ── Danger ── */}
          <div className="sep" />
          <button className="del" disabled={lines.length <= 1} onClick={() => { removeRow(contextMenu.refUuid); closeCtx(); }}>
            <span className="ico">🗑</span><span className="lbl">Delete row</span><kbd>Ctrl Del</kbd></button>
        </div>
      ) : null}

      {/* A CONFIRMED / cancelled document hides the whole editing menu, but
          "why did we quote this?" is asked about exactly those documents. This
          is the same action, alone, with nothing that could mutate the row. */}
      {contextMenu && ro && ctxRow ? (
        <div className="pfg-ctx" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={(e) => e.stopPropagation()}>
          <span className="grp">Explain</span>
          <button onClick={() => { setRationaleTarget(contextMenu.refUuid); closeCtx(); }}>
            <span className="ico">₹</span><span className="lbl">Why this rate?</span></button>
        </div>
      ) : null}

      <PriceRationaleModal
        open={!!rationaleTarget} onClose={() => setRationaleTarget(null)}
        line={rationaleTarget ? treeRows.find((r) => r.refUuid === rationaleTarget) : null}
        priceList={dropdowns && dropdowns.priceList}
      />

      <LamiConfigModal
        open={!!lamiTarget} onClose={() => setLamiTarget(null)} dropdowns={dropdowns}
        row={lamiTarget ? treeRows.find((r) => r.refUuid === lamiTarget) : null}
        onConfirm={(parentPatch, block) => { replaceSubtree(lamiTarget, parentPatch, block); setLamiTarget(null); }}
      />
      <DguConfigModal
        open={!!dguTarget} onClose={() => setDguTarget(null)} dropdowns={dropdowns}
        lamiDgu={dguTarget ? dguTarget.lamiDgu : false}
        row={dguTarget ? treeRows.find((r) => r.refUuid === dguTarget.refUuid) : null}
        onConfirm={(parentPatch, block) => { replaceSubtree(dguTarget.refUuid, parentPatch, block); setDguTarget(null); }}
      />
      <GlassConfigModal
        open={!!glassCfgTarget} onClose={() => setGlassCfgTarget(null)}
        row={glassCfgTarget ? treeRows.find((r) => r.refUuid === glassCfgTarget) : null}
      />
      <NarrationModal
        open={!!narrTarget} onClose={() => setNarrTarget(null)}
        value={narrTarget ? (treeRows.find((r) => r.refUuid === narrTarget) || {}).narration || '' : ''}
        onSave={(text) => { updateRow(narrTarget, { narration: text }); setNarrTarget(null); }}
      />
      <EdgePickerModal
        open={!!edgeTarget} onClose={() => setEdgeTarget(null)}
        row={edgeTarget ? treeRows.find((r) => r.refUuid === edgeTarget) : null}
        header={header}
        onSave={(edgeDetail) => { updateRow(edgeTarget, { edgeDetail }); setEdgeTarget(null); }}
      />
      <Modal
        title="Add multiple rows"
        open={!!addRowsTarget}
        onCancel={() => setAddRowsTarget(null)}
        onOk={() => {
          if (addRowsTarget) insertRows(addRowsTarget.refUuid, 'below', addRowsCount);
          setAddRowsTarget(null);
        }}
        okText="Add rows"
        okButtonProps={{ disabled: !(Number(addRowsCount) >= 1) }}
        width={360}
        destroyOnClose
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>Number of rows to add:</span>
          <InputNumber
            min={1}
            max={MAX_LINES}
            precision={0}
            value={addRowsCount}
            autoFocus
            style={{ width: 110 }}
            onChange={(v) => setAddRowsCount(v)}
            onPressEnter={() => {
              if (addRowsTarget && Number(addRowsCount) >= 1) {
                insertRows(addRowsTarget.refUuid, 'below', addRowsCount);
                setAddRowsTarget(null);
              }
            }}
          />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
          Blank rows are added below the selected row.
        </div>
      </Modal>
      <Modal
        title="Copy multiple rows"
        open={!!copyRowsTarget}
        onCancel={() => setCopyRowsTarget(null)}
        onOk={() => {
          if (copyRowsTarget && Number(copyRowsCount) >= 1) duplicateRow(copyRowsTarget.refUuid, copyRowsCount);
          setCopyRowsTarget(null);
        }}
        okText="Copy rows"
        okButtonProps={{ disabled: !(Number(copyRowsCount) >= 1) }}
        width={380}
        destroyOnClose
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>Number of copies:</span>
          <InputNumber
            min={1}
            max={MAX_LINES}
            precision={0}
            value={copyRowsCount}
            autoFocus
            style={{ width: 110 }}
            onChange={(v) => setCopyRowsCount(v)}
            onPressEnter={() => {
              if (copyRowsTarget && Number(copyRowsCount) >= 1) {
                duplicateRow(copyRowsTarget.refUuid, copyRowsCount);
                setCopyRowsTarget(null);
              }
            }}
          />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
          Exact copies of the {copyRowsTarget && copyRowsTarget.hasChildren ? 'row and its layers' : 'row'} — item,
          sizes, quantity, rates, glass spec and fabrication — are added below it.
          Each copy recalculates on its own.
        </div>
      </Modal>
      <Modal
        title="Copy unit down"
        open={!!copyUnitTarget}
        onCancel={() => setCopyUnitTarget(null)}
        onOk={() => {
          if (!copyUnitTarget) { setCopyUnitTarget(null); return; }
          const plan = planUnitCopy(lines, copyUnitTarget.refUuid);
          if (plan) {
            const blockLen = plan.sourceEnd - plan.sIdx;
            const chosen = plan.targets.slice(0, Number(copyUnitCount) || 0);
            const delta = chosen.reduce((s, t) => s + (blockLen - (t.end - t.idx)), 0);
            if (lines.length + delta > MAX_LINES) {
              message.warning(`This would exceed the ${MAX_LINES}-line limit — reduce the number of rows.`);
              return;   // keep the prompt open so the count can be lowered
            }
            copyUnitDown(copyUnitTarget.refUuid, copyUnitCount);
          }
          setCopyUnitTarget(null);
        }}
        okText="Copy"
        okButtonProps={{ disabled: !(Number(copyUnitCount) >= 1) }}
        width={420}
        destroyOnClose
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>Copy this unit onto the next</span>
          <InputNumber
            min={1}
            max={copyUnitTarget ? copyUnitTarget.max : 1}
            precision={0}
            value={copyUnitCount}
            autoFocus
            style={{ width: 90 }}
            onChange={(v) => setCopyUnitCount(v)}
          />
          <span>cut-piece row{Number(copyUnitCount) === 1 ? '' : 's'} below.</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
          Each target row in this sheet group is replaced with an exact copy of the unit
          (layers, sizes, quantity, fabrication and rates). Stock sheet sizes and rows
          outside the group are never touched.
        </div>
      </Modal>
    </div>
  );
}
